import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Label, Pie, PieChart, XAxis, YAxis } from "recharts";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  BriefcaseBusiness,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  ListChecks,
  Percent,
  RefreshCw,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  DateRangeField,
  MultiSelectField,
  PageLoader,
  Pagination,
  SearchBar,
  StatusBadge,
  getAvatarInitials,
  tabPanelProps,
} from "../components/ui";
import DashboardTabs, {
  DASHBOARD_PAGE_TABS,
  DASHBOARD_TABS_ID,
  DASHBOARD_TAB_SECOES,
} from "../components/DashboardTabs";
import { getUsuarioLogado } from "../services/LoginService";
import { buscarDashboardGestaoComercial, buscarProducaoParceiros } from "../services/dashboardService";
import {
  DASHBOARD_REFRESH_REQUEST_EVENT,
  clearDashboardRefreshState,
  formatRefreshCountdown,
  publishDashboardRefreshState,
} from "../services/dashboardRefreshService";
import usePageFilters from "../hooks/usePageFilters";
import FiltersSheet from "../components/FiltersSheet";
import { listarBancosAtivos, listarConveniosAtivos } from "../services/joinService";
import { consultarRelatorioSessoes, listarTodosUsuariosAtivos } from "../services/usuarioService";
import { listarParceiros } from "../services/parceiroService";
import {
  normalizeDashboardContractStatus,
  normalizeDashboardEvolucao,
  normalizeDashboardResumo,
  normalizeRankingBancos,
  normalizeRankingConvenios,
  normalizeRankingParceiros,
} from "../utils/dashboardSupervisor";
import { addDaysIso, getCurrentBusinessMonthRange, getTodayIso } from "../utils/dateFilters";
import { formatDateTime, formatElapsedSeconds } from "../utils/session";
import { SwalWithTheme } from "../utils/sweetalert";
import NumberFlowValue from "../components/ui/NumberFlowValue";
import { kpiGradientStyle } from "../utils/kpiCard";
import PreFiltroField from "../components/PreFiltroField";
import CampanhaMetaBar, { CampanhaIcone, corDaCampanha } from "../components/CampanhaMetaBar";
import { listarCampanhasVigentes } from "../services/campanhaService";

const STORAGE_KEY = "dashboard-gestao-comercial:filtros:v2";
const DASHBOARD_AUTO_REFRESH_SECONDS = 30;
const RANKING_PAGE_SIZE = 10;
const TYPE_ORDER = { NOVO: 0, PORT: 1, REFIN: 2 };
const TIPO_PERIODO_OPTIONS = [
  {
    value: "APROVACAO",
    label: "Aprovação",
    helper: "Valores por aprovação. Contratos continuam vindo de cadastro.",
  },
  {
    value: "CADASTRO",
    label: "Cadastro",
    helper: "Valores e contratos pelo período de cadastro.",
  },
];
const PARTNER_RANKING_OPTIONS = [
  { key: "BRUTO", label: "Bruto" },
  { key: "LIQUIDO", label: "Líquido" },
  { key: "COMISSIONADO", label: "Comissionado" },
  { key: "CONTRATOS", label: "Contratos" },
];
const AREA_WINDOW_OPTIONS = [
  { value: "7D", label: "7 dias", days: 7 },
  { value: "15D", label: "15 dias", days: 15 },
  { value: "30D", label: "30 dias", days: 30 },
  { value: "90D", label: "90 dias", days: 90 },
  { value: "ALL", label: "Todo período", days: null },
];
const CONTRACT_STATUS_META = {
  aprovados: {
    label: "Aprovados",
    helper: "No período",
    icon: CheckCircle2,
    // Verde de sucesso, o mesmo das tags. Pedido do dono: aprovados verde, negados amarelo.
    color: "hsl(var(--badge-success))",
    accent: "hsl(var(--badge-success))",
  },
  cancelados: {
    label: "Cancelados",
    helper: "No período",
    icon: XCircle,
    // Vermelho vivo das tags, igual nos dois temas (o --destructive do escuro é mais apagado).
    color: "hsl(var(--badge-danger))",
    accent: "hsl(var(--badge-danger))",
  },
  negados: {
    label: "Negados",
    helper: "No período",
    icon: AlertTriangle,
    // Amarelo da marca (#ffb400, fixo nos dois temas: o mesmo par da aba ativa da sidebar). Não o
    // gold, que no claro escurece para #db9500. Cartão com texto escuro (ink), porque branco não se lê.
    color: "hsl(var(--sidebar-primary))",
    accent: "hsl(var(--sidebar-primary))",
    ink: "dark",
  },
  emAndamento: {
    label: "Em andamento",
    helper: "Em processamento",
    icon: Clock3,
    color: "var(--chart-1)",
    accent: "var(--chart-1)",
  },
};

// As abas vivem em components/DashboardTabs.jsx, porque Comissões desenha a mesma barra. A aba
// ativa fica na URL (?aba=), então recarregar ou compartilhar o link mantém a aba.

// Cada aba pede ao servidor só a seção dela, e o que não foi pedido volta nulo. Guardar o bloco
// anterior faz a aba já vista aparecer preenchida na volta, enquanto a consulta nova acontece.
function mergeDashboardSections(previous, incoming) {
  if (!previous) return incoming;
  const merged = { ...previous, ...incoming };
  Object.keys(incoming || {}).forEach((key) => {
    if (incoming[key] == null && previous[key] != null) {
      merged[key] = previous[key];
    }
  });
  return merged;
}

// Paleta categórica (identidade de série), definida em theme.css (--chart-1..8) e validada com o
// validador da skill dataviz nos dois temas. Regra: a cor segue a entidade (o cartão e a linha do
// gráfico de "Produção bruta" têm a mesma cor). Nunca gerar nem repetir uma 9ª cor — o excedente
// vira "Outros" em neutro.
const CHART_CATEGORICAL = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
  "var(--chart-7)",
  "var(--chart-8)",
];
const CHART_NEUTRAL = "hsl(var(--muted-foreground))";
// Tarefas usam tokens semânticos. Contratos (definido pelo dono em 2026-09-15): aprovados = verde,
// cancelados = vermelho, negados = amarelo da marca, em andamento = azul da produção bruta.
// Mesmas cores vivas das tags e dos cartões de tarefas (a cor segue a entidade): o --success/--warning
// do escuro são tons pastel, que sumiam ao lado dos cartões em degradê.
const STATUS_COLOR = {
  success: "hsl(var(--badge-success))",
  warning: "hsl(var(--sidebar-primary))",
  info: "hsl(var(--badge-info))",
  destructive: "hsl(var(--badge-danger))",
};

function formatIsoToBr(value) {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${day}/${month}/${year}` : "";
}

// Cada aba mostra só os filtros que de fato mudam o que ela exibe (verificado no
// DashboardComercialService): bancos, convênios, tipo de produto, supervisor e período base só
// afetam os valores da Join; as tarefas usam apenas o período. Mostrar filtros inertes numa aba
// faz a pessoa mudar algo, não ver efeito e achar que quebrou.
function DashboardFiltersForm({
  tab = "vendas",
  filters,
  onChangeFilters,
  todayIso,
  isAdmin,
  supervisorOptions,
  bancoOptions,
  convenioOptions,
  tipoProdutoOptions,
  sessaoRelatorioRange,
  onChangeSessaoRelatorioRange,
  className = "grid gap-5",
}) {
  // Qual pre-filtro esta marcado e estado da propria tela de filtros: o que vale para a consulta
  // sao os vetores que ele escreve em `filters`.
  const [preFiltroIds, setPreFiltroIds] = useState([]);
  const periodoField = (
    <div className="space-y-1.5">
      <DateRangeField
        label="Período analisado"
        from={filters.dataInicio}
        to={filters.dataFim}
        maxDate={todayIso}
        onFromChange={(event) => onChangeFilters((prev) => ({ ...prev, dataInicio: event.target.value }))}
        onToChange={(event) => onChangeFilters((prev) => ({ ...prev, dataFim: event.target.value }))}
      />
      <p className="text-xs text-muted-foreground">O período vale para as abas Vendas e Tarefas.</p>
    </div>
  );

  if (tab === "tarefas") {
    return <div className={className}>{periodoField}</div>;
  }

  if (tab === "equipe") {
    return (
      <div className={className}>
        {isAdmin ? (
          <DateRangeField
            label="Período do relatório de sessões"
            from={sessaoRelatorioRange.from}
            to={sessaoRelatorioRange.to}
            maxDate={todayIso}
            onFromChange={(event) => onChangeSessaoRelatorioRange((prev) => ({ ...prev, from: event.target.value }))}
            onToChange={(event) => onChangeSessaoRelatorioRange((prev) => ({ ...prev, to: event.target.value }))}
          />
        ) : (
          <p className="text-sm text-muted-foreground">Esta aba não tem filtros.</p>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {periodoField}

      <PreFiltroField
        value={preFiltroIds}
        onChange={setPreFiltroIds}
        onApply={(vetores) => onChangeFilters((prev) => ({ ...prev, ...vetores }))}
      />

      <MultiSelectField
        label="Bancos"
        placeholder="Todos os bancos"
        options={bancoOptions}
        selectedValues={filters.bancosIds}
        onChange={(values) => onChangeFilters((prev) => ({ ...prev, bancosIds: values }))}
      />

      <MultiSelectField
        label="Convênios"
        placeholder="Todos os convênios"
        options={convenioOptions}
        selectedValues={filters.conveniosIds}
        onChange={(values) => onChangeFilters((prev) => ({ ...prev, conveniosIds: values }))}
      />

      <MultiSelectField
        label="Tipo de produto"
        placeholder="Todos os tipos de produto"
        options={tipoProdutoOptions}
        selectedValues={filters.tiposConvenio}
        onChange={(values) => onChangeFilters((prev) => ({ ...prev, tiposConvenio: values }))}
      />

      <MultiSelectField
        label="Supervisor"
        placeholder="Todos os supervisores"
        options={supervisorOptions}
        selectedValues={filters.supervisorIds}
        onChange={(values) => onChangeFilters((prev) => ({ ...prev, supervisorIds: values }))}
      />

      <div className="space-y-1.5 min-w-0">
        <label className="block text-sm font-medium text-foreground">Período base</label>
        <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-input bg-background p-1 shadow-sm">
          {TIPO_PERIODO_OPTIONS.map((option) => {
            const active = filters.tipoPeriodo === option.value;
            const Icon = option.value === "APROVACAO" ? CheckCircle2 : BriefcaseBusiness;
            return (
              <button
                key={option.value}
                type="button"
                title={option.label}
                aria-label={`Período base ${option.label}`}
                aria-pressed={active}
                onClick={() => onChangeFilters((prev) => ({ ...prev, tipoPeriodo: option.value }))}
                className={
                  active
                    ? "flex h-10 items-center justify-center gap-2 rounded-md border border-transparent bg-action px-3 text-action-foreground shadow-sm transition"
                    : "flex h-10 items-center justify-center gap-2 rounded-md border border-transparent px-3 text-muted-foreground transition hover:border-border/70 hover:bg-background/80 hover:text-foreground"
                }
              >
                <Icon className="h-4 w-4" />
                <span className="truncate text-sm font-medium">{option.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Filtros padrão do dashboard: usados ao abrir sem sessão salva e pelo "Limpar filtros".
function getDefaultFilters() {
  const businessMonth = getCurrentBusinessMonthRange();
  return {
    dataInicio: businessMonth.from,
    dataFim: businessMonth.to,
    bancosIds: [],
    conveniosIds: [],
    tiposConvenio: [],
    responsavelIds: [],
    supervisorIds: [],
    tipoPeriodo: "APROVACAO",
  };
}

// Período padrão do relatório de sessões (aba Equipe): últimos 30 dias.
function getDefaultSessaoRelatorioRange() {
  const today = getTodayIso();
  const from = addDaysIso(today, -30);
  return { from: from || today, to: today };
}

function loadFilters() {
  const fallback = getDefaultFilters();

  if (typeof window === "undefined") return fallback;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      ...fallback,
      ...parsed,
      // Período vazio salvo na sessão (o "Limpar" antigo gravava datas vazias) quebrava a consulta,
      // que exige dataInicio e dataFim. Volta ao padrão.
      dataInicio: parsed?.dataInicio || fallback.dataInicio,
      dataFim: parsed?.dataFim || fallback.dataFim,
      bancosIds: Array.isArray(parsed?.bancosIds) ? parsed.bancosIds.map(String) : [],
      conveniosIds: Array.isArray(parsed?.conveniosIds) ? parsed.conveniosIds.map(String) : [],
      tiposConvenio: Array.isArray(parsed?.tiposConvenio) ? parsed.tiposConvenio.map(String) : [],
      responsavelIds: Array.isArray(parsed?.responsavelIds) ? parsed.responsavelIds.map(String) : [],
      supervisorIds: Array.isArray(parsed?.supervisorIds) ? parsed.supervisorIds.map(String) : [],
      tipoPeriodo:
        TIPO_PERIODO_OPTIONS.some((option) => option.value === String(parsed?.tipoPeriodo || "").trim().toUpperCase())
          ? String(parsed?.tipoPeriodo || "").trim().toUpperCase()
          : fallback.tipoPeriodo,
    };
  } catch {
    return fallback;
  }
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("pt-BR");
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

function normalizeDateValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.includes("T") ? raw.slice(0, 10) : raw;
}

function parseDate(value) {
  const normalized = normalizeDateValue(value);
  if (!normalized) return null;
  const date = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isAdminUser(user) {
  return String(user?.cargo || "").trim().toLowerCase() === "admin";
}

function buildScopedUserList(user) {
  return user ? [user] : [];
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function buildContinuousTimeline(points = [], from, to, granularidade = "DIA") {
  const start = parseDate(from);
  const end = parseDate(to);

  if (!start || !end || start > end) {
    return points;
  }

  const normalizedStart = granularidade === "MES" ? startOfMonth(start) : new Date(start);
  const normalizedEnd = granularidade === "MES" ? startOfMonth(end) : new Date(end);
  const pointsMap = new Map(points.map((point) => [normalizeDateValue(point?.data), point]));
  const filled = [];
  const current = new Date(normalizedStart);

  while (current <= normalizedEnd) {
    const key = formatIsoDate(current);
    filled.push({
      ...(pointsMap.get(key) || {}),
      data: key,
    });

    if (granularidade === "MES") {
      current.setMonth(current.getMonth() + 1);
    } else {
      current.setDate(current.getDate() + 1);
    }
  }

  return filled;
}

function formatProductionDateLabel(value, granularidade) {
  const date = parseDate(value);
  if (!date) return normalizeDateValue(value);
  if (granularidade === "MES") {
    return date.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
  }
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function formatProductionDayTickLabel(value) {
  const date = parseDate(value);
  if (!date) return normalizeDateValue(value);
  return date.toLocaleDateString("pt-BR", { day: "2-digit" });
}

function formatProductionTimelineTickLabel(value) {
  const date = parseDate(value);
  if (!date) return normalizeDateValue(value);
  const day = date.toLocaleDateString("pt-BR", { day: "numeric" });
  const month = date.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
  return `${day} ${month}`;
}

function formatProductionTooltipLabel(value, granularidade) {
  const date = parseDate(value);
  if (!date) return normalizeDateValue(value);
  if (granularidade === "MES") {
    return date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  }
  return date.toLocaleDateString("pt-BR");
}

function buildChartGeometry(points, selector, width = 660, height = 280, padding = 28) {
  if (!Array.isArray(points) || points.length === 0) {
    return { linePath: "", areaPath: "", items: [], max: 0, width, height, padding };
  }

  const values = points.map((point) => Number(selector(point) || 0));
  const max = Math.max(...values, 0);
  const safeMax = max || 1;
  const innerWidth = width - padding * 2;
  const innerHeight = height - padding * 2;
  const stepX = points.length === 1 ? 0 : innerWidth / (points.length - 1);

  const items = points.map((point, index) => {
    const value = Number(selector(point) || 0);
    const x = padding + stepX * index;
    const y = padding + innerHeight - (value / safeMax) * innerHeight;
    return { ...point, value, x, y };
  });

  const linePath = items
    .map((item, index) => `${index === 0 ? "M" : "L"} ${item.x.toFixed(2)} ${item.y.toFixed(2)}`)
    .join(" ");

  const areaPath = [
    linePath,
    `L ${items[items.length - 1].x.toFixed(2)} ${(height - padding).toFixed(2)}`,
    `L ${items[0].x.toFixed(2)} ${(height - padding).toFixed(2)}`,
    "Z",
  ].join(" ");

  return { linePath, areaPath, items, max, width, height, padding };
}

function buildTickItems(items = [], granularidade = "DIA") {
  if (items.length <= 4 || granularidade === "MES") {
    return items;
  }

  const step = Math.max(1, Math.floor(items.length / 5));
  return items.filter((_, index) => index === 0 || index === items.length - 1 || index % step === 0);
}

function getTickLabelStyle(item, index, ticks, geometry) {
  const left = `${(item.x / geometry.width) * 100}%`;

  if (index === 0) {
    return { left, transform: "translateX(0)" };
  }

  if (index === ticks.length - 1) {
    return { left, transform: "translateX(-100%)" };
  }

  return { left, transform: "translateX(-50%)" };
}

// accent é uma cor CSS (token). A faixa de 3px no topo e o ícone usam a MESMA cor da série ou do
// status que o número representa no gráfico logo abaixo — a cor segue a entidade. Neutro quando o
// número não tem série correspondente.
// Cartão de KPI em dois tons (decisão do dono em 2026-09-15):
// - "gradient": fileiras de Produção e Contratos. Degradê vivo da cor da entidade (clareia no início,
//   escurece pouco no fim) com brilho no canto e texto branco. Sem color-mix, fica a cor sólida.
//   Acento precisa ser uma cor média (neutro vira bloco preto); cor clara como o amarelo usa
//   ink="dark", texto #171717 sem sombra.
//   É o padrão desde 2026-09-15: o cartão claro deixou Tarefas e Equipe apagados no tema escuro.
// - "soft": cartão claro com um véu da cor no canto e ícone tingido. Mantido, mas sem uso no dashboard.
function MetricTile({ icon: Icon, label, value, helper, accent = CHART_NEUTRAL, tone = "gradient", ink = "light" }) {
  if (tone === "gradient") {
    const base = !accent || accent === CHART_NEUTRAL ? "hsl(var(--kpi-neutral))" : accent;
    const darkInk = ink === "dark";
    return (
      <article
        className={cn(
          "relative min-w-0 overflow-hidden rounded-2xl shadow-md transition-shadow hover:shadow-lg",
          darkInk ? "text-kpi-ink" : "text-kpi-foreground"
        )}
        style={kpiGradientStyle(base, ink)}
      >
        <div className="relative flex min-w-0 items-start justify-between gap-3 p-5">
          <div className="min-w-0 space-y-1.5">
            <p className={cn("text-[11px] font-semibold uppercase tracking-[0.16em]", darkInk ? "text-kpi-ink/80" : "text-kpi-foreground/90")}>
              {label}
            </p>
            <p className="max-w-full break-all font-display text-[28px] font-semibold leading-tight">{value}</p>
            {helper ? <p className={cn("text-xs", darkInk ? "text-kpi-ink/75" : "text-kpi-foreground/85")}>{helper}</p> : null}
          </div>
          <Icon className={cn("h-6 w-6 shrink-0", darkInk ? "text-kpi-ink/85" : "text-kpi-foreground/90")} aria-hidden="true" />
        </div>
      </article>
    );
  }

  return (
    <article
      className="relative min-w-0 overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
      style={{ backgroundImage: `linear-gradient(135deg, color-mix(in srgb, ${accent} 16%, transparent) 0%, transparent 60%)` }}
    >
      <div className="relative flex min-w-0 items-start justify-between gap-3 p-5">
        <div className="min-w-0 space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
          <p className="max-w-full break-all font-display text-[28px] font-semibold leading-tight text-foreground">{value}</p>
          {helper ? <p className="text-xs text-muted-foreground">{helper}</p> : null}
        </div>
        <Icon className="h-6 w-6 shrink-0" style={{ color: accent }} aria-hidden="true" />
      </div>
    </article>
  );
}

// Barra de ranking com degradê horizontal da própria cor, mais leve no início. A mistura é com o
// trilho (muted), não com transparente, para o fallback sólido de backgroundColor não aparecer por trás.
// Cor chapada de proposito: o degrade anterior clareava a ponta da barra e, no tema escuro, dava
// aparencia de neon.
function rankingBarStyle(color, widthPercent) {
  return {
    width: `${widthPercent}%`,
    backgroundColor: color,
  };
}

function ChartTooltipBubble({ title, lines, left, top }) {
  return (
    <div
      className="pointer-events-none absolute z-20 min-w-[160px] rounded-xl border border-border bg-popover px-3 py-2.5 text-left shadow-[0_18px_48px_-12px_rgba(0,0,0,0.72)] ring-1 ring-black/10"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        transform: "translate(-50%, -110%)",
        backgroundColor: "hsl(var(--chart-tooltip-background))",
        borderColor: "hsl(var(--chart-tooltip-border))",
        opacity: 1,
      }}
    >
      <p className="text-xs font-semibold text-popover-foreground">{title}</p>
      <div className="mt-2 space-y-1">
        {lines.map((line) => (
          <div key={line.label} className="flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: line.color }} />
              <span className="text-muted-foreground">{line.label}</span>
            </div>
            <span className="font-semibold tabular-nums text-popover-foreground">{line.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function getContractsCount(item) {
  return Number(item?.quantidadeContratosTotal || item?.quantidadeContratos || item?.quantidade || 0);
}

function formatContracts(count) {
  return `${formatNumber(count)} contrato${count === 1 ? "" : "s"}`;
}

function AreaTrendCard({ title, description, points, metrics, visibleMetrics, onToggleMetric }) {
  const [windowPreset, setWindowPreset] = useState("ALL");
  // useId devolve ":r0:", que quebra o url(#...) do SVG.
  const gradientId = useId().replace(/:/g, "");
  const visibleMetricSet = useMemo(() => new Set(visibleMetrics), [visibleMetrics]);
  const chartData = useMemo(
    () =>
      points.map((point) => ({
        data: point?.data || "",
        ...metrics.reduce((acc, metric) => {
          const rawValue = Number(point?.[metric.key] || 0);
          acc[metric.key] = rawValue;
          acc[`${metric.key}Display`] = visibleMetricSet.has(metric.key) ? rawValue : 0;
          return acc;
        }, {}),
      })),
    [metrics, points, visibleMetricSet]
  );
  const availableWindowOptions = useMemo(() => {
    const total = chartData.length;
    return AREA_WINDOW_OPTIONS.filter((option) => option.days == null || total > option.days);
  }, [chartData.length]);
  const effectiveWindowPreset = useMemo(
    () => (availableWindowOptions.some((option) => option.value === windowPreset) ? windowPreset : "ALL"),
    [availableWindowOptions, windowPreset]
  );
  const visibleChartData = useMemo(() => {
    const selectedOption = AREA_WINDOW_OPTIONS.find((option) => option.value === effectiveWindowPreset);
    if (!selectedOption?.days) {
      return chartData;
    }
    return chartData.slice(-selectedOption.days);
  }, [chartData, effectiveWindowPreset]);
  const chartConfig = useMemo(
    () =>
      metrics.reduce((acc, metric) => {
        acc[metric.key] = {
          label: metric.label,
          color: metric.color,
        };
        return acc;
      }, {}),
    [metrics]
  );
  const visibleSeriesMax = useMemo(
    () =>
      Math.max(
        1,
        ...visibleChartData.flatMap((point) =>
          metrics
            .filter((metric) => visibleMetricSet.has(metric.key))
            .map((metric) => Number(point?.[`${metric.key}Display`] || 0))
        )
      ),
    [metrics, visibleChartData, visibleMetricSet]
  );
  const renderedMetrics = useMemo(
    () =>
      [...metrics].sort((left, right) => {
        const leftVisible = visibleMetricSet.has(left.key) ? 1 : 0;
        const rightVisible = visibleMetricSet.has(right.key) ? 1 : 0;
        return leftVisible - rightVisible;
      }),
    [metrics, visibleMetricSet]
  );
  // Nome da série no fim de cada linha: a cor não carrega a identidade sozinha, e verde-água e ciano
  // ficam abaixo de 3:1 no tema claro. Quando os valores finais são próximos, afasta os rótulos
  // (estimativa em px da área de plotagem do container de 250px).
  const lastIndex = visibleChartData.length - 1;
  const endLabelOffsets = useMemo(() => {
    const plotHeight = 205;
    const minGap = 15;
    const ends = metrics
      .filter((metric) => visibleMetricSet.has(metric.key))
      .map((metric) => ({
        key: metric.key,
        y: plotHeight - (Number(visibleChartData[lastIndex]?.[`${metric.key}Display`] || 0) / visibleSeriesMax) * plotHeight,
      }))
      .sort((left, right) => left.y - right.y);
    const offsets = {};
    let previous = null;
    ends.forEach((end) => {
      const placed = previous === null ? end.y : Math.max(end.y, previous + minGap);
      offsets[end.key] = placed - end.y;
      previous = placed;
    });
    return offsets;
  }, [lastIndex, metrics, visibleChartData, visibleMetricSet, visibleSeriesMax]);

  const tooltipContent = ({ active, payload, label }) => {
    if (!active) {
      return null;
    }

    const filteredPayload = (Array.isArray(payload) ? payload : []).filter((entry) =>
      visibleMetricSet.has(String(entry?.dataKey || "").replace(/Display$/, ""))
    );

    if (!filteredPayload.length) {
      return null;
    }

    return (
      <ChartTooltipContent
        active={active}
        payload={filteredPayload}
        label={label}
        labelFormatter={(value) => formatProductionTooltipLabel(value, "DIA")}
        formatter={(value, _name, entry) => [
          formatCurrency(value),
          chartConfig[String(entry?.dataKey || "").replace(/Display$/, "")]?.label || entry?.name,
        ]}
        indicator="dot"
      />
    );
  };

  useEffect(() => {
    if (!availableWindowOptions.some((option) => option.value === windowPreset)) {
      setWindowPreset("ALL");
    }
  }, [availableWindowOptions, windowPreset]);

  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
        <div className="grid flex-1 gap-1">
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>

        {availableWindowOptions.length > 1 ? (
          <div className="hidden items-center gap-2 sm:flex">
            <span className="text-xs font-medium text-muted-foreground">Janela</span>
            <div className="flex flex-wrap items-center gap-1 rounded-xl border border-border bg-background/55 p-1">
              {availableWindowOptions.map((option) => {
                const active = option.value === effectiveWindowPreset;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setWindowPreset(option.value)}
                    className={
                      active
                        ? "rounded-lg bg-foreground px-2.5 py-1 text-xs font-semibold text-background transition"
                        : "rounded-lg px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:bg-background hover:text-foreground"
                    }
                    aria-pressed={active}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </CardHeader>

      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Séries exibidas">
          {metrics.map((item) => {
            const active = visibleMetrics.includes(item.key);
            return (
              <button
                key={item.key}
                type="button"
                aria-pressed={active}
                onClick={() => onToggleMetric(item.key)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  active
                    ? "border-border bg-card text-foreground shadow-sm"
                    : "border-dashed border-border bg-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full border-2"
                  style={{ background: active ? item.color : "transparent", borderColor: item.color }}
                  aria-hidden="true"
                />
                {item.label}
              </button>
            );
          })}
        </div>

        {availableWindowOptions.length > 1 ? (
          <div className="mb-4 flex flex-wrap gap-2 sm:hidden">
            {availableWindowOptions.map((option) => {
              const active = option.value === effectiveWindowPreset;
              return (
                <button
                  key={`mobile-${option.value}`}
                  type="button"
                  onClick={() => setWindowPreset(option.value)}
                  className={
                    active
                      ? "rounded-md border border-foreground bg-foreground px-3 py-1.5 text-xs font-semibold text-background transition"
                      : "rounded-md border border-border bg-background text-muted-foreground px-3 py-1.5 text-xs font-medium transition hover:text-foreground"
                  }
                  aria-pressed={active}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        ) : null}

        {visibleChartData.length ? (
          <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
            <AreaChart
              data={visibleChartData}
              margin={{
                left: 4,
                right: 104,
                top: 12,
                bottom: 0,
              }}
            >
              <defs>
                {metrics.map((metric) => (
                  <linearGradient key={metric.key} id={`${gradientId}-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" style={{ stopColor: metric.color, stopOpacity: 0.42 }} />
                    <stop offset="95%" style={{ stopColor: metric.color, stopOpacity: 0.03 }} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke="hsl(var(--border) / 0.55)" />
              <XAxis
                dataKey="data"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value) => formatProductionTimelineTickLabel(value)}
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 12 }}
              />
              <YAxis hide allowDecimals={false} domain={[0, visibleSeriesMax]} />
              <ChartTooltip cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }} content={tooltipContent} />
              {renderedMetrics.map((metric) => {
                const visible = visibleMetricSet.has(metric.key);
                // Área com degradê da cor da série, sobreposta (não empilhada). O degradê some quase
                // todo no pé (3%), então a série de trás continua visível pela linha de 2px.
                return (
                  <Area
                    key={metric.key}
                    dataKey={`${metric.key}Display`}
                    name={metric.label}
                    type="linear"
                    fill={`url(#${gradientId}-${metric.key})`}
                    fillOpacity={visible ? 1 : 0}
                    stroke={metric.color}
                    strokeWidth={2}
                    strokeOpacity={visible ? 1 : 0.28}
                    isAnimationActive={false}
                    animationDuration={280}
                    animationEasing="ease-in-out"
                    animationBegin={0}
                    dot={(props) =>
                      visible && props.index === lastIndex ? (
                        <circle
                          key={`${metric.key}-end-dot`}
                          cx={props.cx}
                          cy={props.cy}
                          r={4}
                          fill={metric.color}
                          stroke="hsl(var(--card))"
                          strokeWidth={2}
                        />
                      ) : (
                        <g key={`${metric.key}-dot-${props.index}`} />
                      )
                    }
                    activeDot={visible ? { r: 4, fill: metric.color, stroke: "hsl(var(--card))", strokeWidth: 2 } : false}
                    label={(props) =>
                      visible && props.index === lastIndex ? (
                        <text
                          key={`${metric.key}-end-label`}
                          x={Number(props.x) + 10}
                          y={Number(props.y) + (endLabelOffsets[metric.key] || 0)}
                          dominantBaseline="middle"
                          fontSize={12}
                          fontWeight={600}
                          fill="hsl(var(--foreground))"
                        >
                          {metric.label}
                        </text>
                      ) : null
                    }
                  />
                );
              })}
            </AreaChart>
          </ChartContainer>
        ) : (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Sem evolução de produção no período selecionado.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ContractStatusStackedBarCard({ title, description, points, granularidade }) {
  const chartData = useMemo(
    () =>
      points.map((point) => ({
        data: point?.data || "",
        aprovados: Number(point?.contratosAprovados || 0),
        cancelados: Number(point?.contratosCancelados || 0),
        negados: Number(point?.contratosNegados || 0),
        emAndamento: Number(point?.contratosEmAndamento || 0),
      })),
    [points]
  );
  const chartConfig = useMemo(
    () => ({
      aprovados: { label: CONTRACT_STATUS_META.aprovados.label, color: CONTRACT_STATUS_META.aprovados.color },
      cancelados: { label: CONTRACT_STATUS_META.cancelados.label, color: CONTRACT_STATUS_META.cancelados.color },
      negados: { label: CONTRACT_STATUS_META.negados.label, color: CONTRACT_STATUS_META.negados.color },
      emAndamento: { label: CONTRACT_STATUS_META.emAndamento.label, color: CONTRACT_STATUS_META.emAndamento.color },
    }),
    []
  );

  return (
    <Card title={title}>
      <div className="space-y-4">
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}

        {chartData.length ? (
          <div className="rounded-3xl border border-border/70 bg-background/40 p-4">
            <ChartContainer config={chartConfig} className="h-[320px] w-full min-w-0">
              <BarChart
                data={chartData}
                margin={{
                  left: 8,
                  right: 8,
                  top: 28,
                  bottom: 8,
                }}
                barGap={6}
                barCategoryGap="26%"
              >
                <CartesianGrid vertical={false} stroke="hsl(var(--border) / 0.55)" />
                <XAxis
                  dataKey="data"
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={16}
                  tickMargin={10}
                  padding={{ left: 12, right: 12 }}
                  tickFormatter={(value) =>
                    granularidade === "DIA"
                      ? formatProductionTimelineTickLabel(value)
                      : formatProductionDateLabel(value, granularidade)
                  }
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  tickFormatter={(value) => formatNumber(value)}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                />
                <ChartTooltip
                  cursor={{ fill: "hsl(var(--border) / 0.16)" }}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) => formatProductionTooltipLabel(value, granularidade)}
                      formatter={(value, _name, entry) => {
                        const total =
                          Number(entry?.payload?.aprovados || 0) +
                          Number(entry?.payload?.cancelados || 0) +
                          Number(entry?.payload?.negados || 0) +
                          Number(entry?.payload?.emAndamento || 0);
                        const numericValue = Number(value || 0);
                        const percent = total > 0 ? (numericValue / total) * 100 : 0;
                        const label = chartConfig?.[entry?.dataKey]?.label || entry?.name;
                        return [`${formatNumber(numericValue)} (${formatPercent(percent)})`, label];
                      }}
                      indicator="dot"
                    />
                  }
                />
                <ChartLegend
                  verticalAlign="top"
                  align="left"
                  content={<ChartLegendContent />}
                  wrapperStyle={{ paddingLeft: 52 }}
                />
                <Bar dataKey="aprovados" stackId="statusContratos" fill="var(--color-aprovados)" maxBarSize={22} isAnimationActive={false} />
                <Bar dataKey="cancelados" stackId="statusContratos" fill="var(--color-cancelados)" maxBarSize={22} isAnimationActive={false} />
                <Bar dataKey="negados" stackId="statusContratos" fill="var(--color-negados)" maxBarSize={22} isAnimationActive={false} />
                <Bar dataKey="emAndamento" stackId="statusContratos" fill="var(--color-emAndamento)" maxBarSize={22} isAnimationActive={false} />
              </BarChart>
            </ChartContainer>
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Sem evolução de status de contratos no período selecionado.
          </div>
        )}
      </div>
    </Card>
  );
}

function OperationalAreaCard({ title, description, points, granularidade }) {
  const chartData = useMemo(
    () =>
      (Array.isArray(points) ? points : []).map((point) => ({
        ...point,
        criadas: Number(point?.criadas || 0),
        concluidas: Number(point?.concluidas || 0),
        canceladas: Number(point?.canceladas || 0),
        atrasadas: Number(point?.atrasadas || 0),
      })),
    [points]
  );

  const chartConfig = useMemo(
    () => ({
      criadas: { label: "Criadas", color: STATUS_COLOR.info },
      concluidas: { label: "Concluídas", color: STATUS_COLOR.success },
      atrasadas: { label: "Entraram em atraso", color: STATUS_COLOR.warning },
      canceladas: { label: "Canceladas", color: STATUS_COLOR.destructive },
    }),
    []
  );

  const hasMovement = useMemo(
    () => chartData.some((point) => Object.keys(chartConfig).some((key) => Number(point?.[key] || 0) > 0)),
    [chartData, chartConfig]
  );

  return (
    <Card title={title}>
      <div className="space-y-4">
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        {hasMovement ? (
          <div className="rounded-3xl border border-border/70 bg-background/40 p-4 overflow-x-hidden">
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <AreaChart
                accessibilityLayer
                data={chartData}
                margin={{ left: 8, right: 12, top: 18, bottom: 6 }}
              >
                <defs>
                  <linearGradient id="tasks-fill-criadas" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-criadas)" stopOpacity="0.38" />
                    <stop offset="100%" stopColor="var(--color-criadas)" stopOpacity="0.06" />
                  </linearGradient>
                  <linearGradient id="tasks-fill-concluidas" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-concluidas)" stopOpacity="0.34" />
                    <stop offset="100%" stopColor="var(--color-concluidas)" stopOpacity="0.06" />
                  </linearGradient>
                  <linearGradient id="tasks-fill-atrasadas" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-atrasadas)" stopOpacity="0.32" />
                    <stop offset="100%" stopColor="var(--color-atrasadas)" stopOpacity="0.06" />
                  </linearGradient>
                  <linearGradient id="tasks-fill-canceladas" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-canceladas)" stopOpacity="0.32" />
                    <stop offset="100%" stopColor="var(--color-canceladas)" stopOpacity="0.06" />
                  </linearGradient>
                </defs>

                <CartesianGrid vertical={false} stroke="hsl(var(--border) / 0.55)" />
                <XAxis
                  dataKey="data"
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={22}
                  tickMargin={10}
                  padding={{ left: 12, right: 12 }}
                  tickFormatter={(value) =>
                    granularidade === "DIA"
                      ? formatProductionTimelineTickLabel(value)
                      : formatProductionDateLabel(value, granularidade)
                  }
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  tickFormatter={(value) => formatNumber(value)}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                />

                <ChartTooltip
                  cursor={{ stroke: "hsl(var(--border) / 0.6)", strokeWidth: 1 }}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) => formatProductionTooltipLabel(value, granularidade)}
                      formatter={(value, _name, entry) => {
                        const payload = entry?.payload || {};
                        const total =
                          Number(payload?.criadas || 0) +
                          Number(payload?.concluidas || 0) +
                          Number(payload?.atrasadas || 0) +
                          Number(payload?.canceladas || 0);
                        const numericValue = Number(value || 0);
                        const percent = total > 0 ? (numericValue / total) * 100 : 0;
                        const label = chartConfig?.[entry?.dataKey]?.label || entry?.name;
                        return [`${formatNumber(numericValue)} (${formatPercent(percent)})`, label];
                      }}
                      indicator="dot"
                    />
                  }
                />
                <ChartLegend
                  verticalAlign="top"
                  align="left"
                  content={<ChartLegendContent />}
                  wrapperStyle={{ paddingLeft: 52 }}
                />

                <Area
                  type="monotone"
                  dataKey="criadas"
                  stroke="var(--color-criadas)"
                  strokeWidth={2.2}
                  fill="url(#tasks-fill-criadas)"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="concluidas"
                  stroke="var(--color-concluidas)"
                  strokeWidth={2.2}
                  fill="url(#tasks-fill-concluidas)"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="atrasadas"
                  stroke="var(--color-atrasadas)"
                  strokeWidth={2.2}
                  fill="url(#tasks-fill-atrasadas)"
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="canceladas"
                  stroke="var(--color-canceladas)"
                  strokeWidth={2.2}
                  fill="url(#tasks-fill-canceladas)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ChartContainer>
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Sem movimentação operacional no período selecionado.
          </div>
        )}
      </div>
    </Card>
  );
}

function StatusOverviewCard({ title, description, items, total }) {
  const max = Math.max(...items.map((item) => Number(item.total || 0)), 0);
  const toneByLabel = {
    Criadas: STATUS_COLOR.info,
    "Concluidas no prazo": STATUS_COLOR.success,
    "Concluidas com atraso": STATUS_COLOR.warning,
    Canceladas: STATUS_COLOR.destructive,
    "Entraram em atraso": STATUS_COLOR.warning,
  };

  return (
    <Card title={title}>
      <div className="space-y-4">
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        {items.length ? (
          items.map((item) => {
            const color = toneByLabel[item.label] || CHART_NEUTRAL;
            return (
              <div key={item.label} className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                    <span className="min-w-0 whitespace-normal break-words text-sm font-medium text-foreground">
                      {item.label}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">{formatNumber(item.total)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {total > 0 ? `${((Number(item.total || 0) / total) * 100).toFixed(1).replace(".", ",")}%` : "0%"}
                    </p>
                  </div>
                </div>
                <div className="h-2.5 rounded-full bg-border/70">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: max > 0 ? `${(Number(item.total || 0) / max) * 100}%` : "0%",
                      background: color,
                    }}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Sem tarefas no período selecionado.
          </div>
        )}
      </div>
    </Card>
  );
}

function TaskBreakdownDonutCard({
  title,
  subtitle,
  items,
  centerLabel = "Total",
  emptyLabel,
  maxSlices = 7,
  headerExtra = null,
}) {
  const pieAnimationDuration = 650;
  // 7 fatias coloridas + "Outros" em neutro: nunca repete nem gera uma 9ª cor.
  const palette = CHART_CATEGORICAL;

  const normalized = useMemo(() => {
    const source = Array.isArray(items) ? items : [];
    return source
      .map((item, index) => ({
        label: String(item?.label || `Item ${index + 1}`).trim() || `Item ${index + 1}`,
        value: Number(item?.value || 0),
      }))
      .filter((item) => Number.isFinite(item.value) && item.value > 0);
  }, [items]);

  const total = useMemo(() => normalized.reduce((sum, item) => sum + item.value, 0), [normalized]);

  const chartData = useMemo(() => {
    if (!normalized.length) return [];
    const sorted = normalized.slice().sort((a, b) => b.value - a.value);
    const sliceLimit = Math.max(1, Number(maxSlices) || 1);
    const top = sorted.slice(0, sliceLimit);
    const rest = sorted.slice(sliceLimit);
    const restValue = rest.reduce((sum, item) => sum + item.value, 0);
    const merged = restValue > 0 ? [...top, { label: "Outros", value: restValue }] : top;
    return merged.map((item, index) => ({
      name: item.label,
      total: item.value,
      fill: item.label === "Outros" ? CHART_NEUTRAL : palette[index],
    }));
  }, [normalized, maxSlices, palette]);

  const topSlice = useMemo(
    () => chartData.reduce((best, slice) => (Number(slice?.total || 0) > (best?.total || 0) ? slice : best), null),
    [chartData]
  );

  const chartConfig = useMemo(() => ({ total: { label: centerLabel } }), [centerLabel]);

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <CardTitle className="truncate">{title}</CardTitle>
            {subtitle ? <CardDescription>{subtitle}</CardDescription> : null}
          </div>
          {headerExtra ? <div className="shrink-0">{headerExtra}</div> : null}
        </div>
      </CardHeader>

      <CardContent className="flex-1 pb-0">
        {chartData.length ? (
          <>
            <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[260px]">
              <PieChart>
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      hideLabel
                      formatter={(value, name, entry) => {
                        const numericValue = Number(value || 0);
                        const percent = total > 0 ? (numericValue / total) * 100 : 0;
                        return [`${formatNumber(numericValue)} (${formatPercent(percent)})`, name || entry?.name];
                      }}
                      indicator="dot"
                    />
                  }
                />
                <Pie
                  data={chartData}
                  dataKey="total"
                  nameKey="name"
                  innerRadius={72}
                  isAnimationActive={false}
                  animationBegin={0}
                  animationDuration={pieAnimationDuration}
                  animationEasing="ease-out"
                  stroke="hsl(var(--card))"
                  strokeWidth={4}
                >
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                        return (
                          <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                            <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-3xl font-bold">
                              {formatNumber(total)}
                            </tspan>
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 22} className="fill-muted-foreground text-xs">
                              {centerLabel}
                            </tspan>
                          </text>
                        );
                      }
                      return null;
                    }}
                  />
                </Pie>
              </PieChart>
            </ChartContainer>

            <div className="mt-4 space-y-2">
              {chartData.map((slice) => {
                const percent = total > 0 ? (Number(slice.total || 0) / total) * 100 : 0;
                return (
                  <div key={slice.name} className="flex items-center justify-between gap-3 text-xs">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: slice.fill }} />
                      <span className="truncate text-muted-foreground">{slice.name}</span>
                    </div>
                    <span className="shrink-0 font-medium text-foreground">
                      {formatNumber(slice.total)} ({formatPercent(percent)})
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        )}
      </CardContent>

      {chartData.length ? (
        <CardFooter className="flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 leading-none font-medium text-foreground">
            Maior concentracao em {topSlice?.name || "N/A"} com {formatNumber(topSlice?.total || 0)}
            <TrendingUp className="h-4 w-4" />
          </div>
          <div className="leading-none text-muted-foreground">Mostrando {chartData.length} fatia(s) no recorte filtrado.</div>
        </CardFooter>
      ) : null}
    </Card>
  );
}

function getPartnerRankingMetricValue(item, mode) {
  switch (mode) {
    case "LIQUIDO":
      return Number(item?.producaoLiquidaTotal || 0);
    case "COMISSIONADO":
      return Number(item?.producaoComissionadaTotal || 0);
    case "CONTRATOS":
      return Number(item?.quantidadeContratosTotal || item?.quantidadeContratos || item?.quantidade || 0);
    case "BRUTO":
    default:
      return Number(item?.producaoBrutaTotal || item?.value || 0);
  }
}

// Ranking de parceiros em lista única (o pódio do top 3 foi removido a pedido), com foto e nome do
// responsável, valor, contratos e barra comparando com o líder. Duas colunas em telas largas.
function PartnerRankingCard({ items, onOpenPartner, rankingMode, onRankingModeChange }) {
  const [page, setPage] = useState(1);

  // Volta ao início só quando a métrica muda. O auto-refresh de 30s recria `items`, e resetar por
  // ele devolvia o usuário à página 1 no meio da leitura.
  useEffect(() => {
    setPage(1);
  }, [rankingMode]);

  const rankingFormatter = rankingMode === "CONTRATOS" ? formatNumber : formatCurrency;
  const rankingDescription = {
    BRUTO: "Ranking por produção bruta.",
    LIQUIDO: "Ranking por produção líquida.",
    COMISSIONADO: "Ranking por comissão.",
    CONTRATOS: "Ranking por contratos.",
  }[rankingMode] || "Ranking de parceiros.";

  const leaderValue = Number(items[0]?.rankingValue || 0);
  const totalPages = Math.max(1, Math.ceil(items.length / RANKING_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageOffset = (currentPage - 1) * RANKING_PAGE_SIZE;
  const pageItems = items.slice(pageOffset, pageOffset + RANKING_PAGE_SIZE);
  // Com mais de uma página, a última é completada com linhas invisíveis: o card mantém a altura e a
  // paginação não sobe ao chegar no fim.
  const slots = totalPages > 1
    ? [...pageItems, ...Array(RANKING_PAGE_SIZE - pageItems.length).fill(null)]
    : pageItems;
  const half = Math.ceil(slots.length / 2);
  const columns = slots.length > 5
    ? [
        { offset: 0, rows: slots.slice(0, half) },
        { offset: half, rows: slots.slice(half) },
      ]
    : [{ offset: 0, rows: slots }];

  const modeSelector = (
    <div key="modo" className="flex flex-wrap gap-1.5" role="group" aria-label="Métrica do ranking">
      {PARTNER_RANKING_OPTIONS.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={option.key === rankingMode}
          onClick={() => onRankingModeChange(option.key)}
          className={cn(
            "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
            option.key === rankingMode
              ? "border-transparent bg-foreground text-background"
              : "border-border bg-background/50 text-muted-foreground hover:text-foreground"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const renderRow = (item, position) => {
    const name = item.partnerName || item.label;
    const share = leaderValue > 0 ? Math.min(100, (Number(item.rankingValue || 0) / leaderValue) * 100) : 0;
    const content = (
      <>
        <span
          className={cn(
            "w-8 shrink-0 pt-0.5 text-right text-sm font-semibold tabular-nums",
            position <= 3 ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {position}º
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate text-sm font-medium text-foreground">{name}</p>
            <p className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{rankingFormatter(item.rankingValue)}</p>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <div className="h-full rounded-full" style={rankingBarStyle("var(--chart-1)", share)} />
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            {item.responsavel ? (
              <span className="flex min-w-0 items-center gap-2">
                <Avatar className="h-7 w-7 shrink-0 rounded-full border border-border">
                  <AvatarImage src={item.responsavel.fotoPerfilUrl} alt={item.responsavel.nome} />
                  <AvatarFallback className="bg-muted text-[10px] text-muted-foreground">
                    {getAvatarInitials(item.responsavel.nome)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-xs text-muted-foreground">{item.responsavel.nome}</span>
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">Responsável não relacionado</span>
            )}
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {formatContracts(getContractsCount(item))}
            </span>
          </div>
        </div>
      </>
    );

    return item.partnerId ? (
      <button
        type="button"
        onClick={() => onOpenPartner(item.partnerId)}
        className="flex w-full items-start gap-3 rounded-lg px-2 py-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {content}
      </button>
    ) : (
      <div className="flex w-full items-start gap-3 px-2 py-3">{content}</div>
    );
  };

  return (
    <Card title="Ranking de parceiros" actions={[modeSelector]}>
      {items.length ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{rankingDescription}</p>

          <div className={cn("grid gap-x-6", columns.length > 1 && "xl:grid-cols-2")}>
            {columns.map((column, columnIndex) => (
              <ol key={`coluna-${columnIndex}`} className="min-w-0 divide-y divide-border">
                {column.rows.map((item, rowIndex) => {
                  const position = pageOffset + column.offset + rowIndex + 1;
                  if (!item) {
                    return (
                      <li key={`vazio-${position}`} className="invisible" aria-hidden="true">
                        {renderRow(items[0], position)}
                      </li>
                    );
                  }
                  return <li key={`${item.label}-${position}`}>{renderRow(item, position)}</li>;
                })}
              </ol>
            ))}
          </div>

          <Pagination
            page={currentPage}
            pageSize={RANKING_PAGE_SIZE}
            totalItems={items.length}
            itemLabel="parceiros"
            onPageChange={setPage}
          />
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nenhum parceiro retornado pela integração no período.
        </div>
      )}
    </Card>
  );
}

// Parceiros mês a mês. Herda os filtros gerais da aba Vendas (bancos, convênios, produto,
// supervisor, responsável e base do período); o card só escolhe a métrica. A data final do filtro
// geral define o último mês — o período em dias não serve direto, "últimos 7 dias" daria um mês só.
// Sempre os últimos 3 meses: 6 e 12 foram retirados em 2026-09-15 por serem pesados demais (uma
// consulta à Join por mês). Cada mês fica em cache: o auto-refresh de 30s só reconsulta o que
// expirou (mês que inclui hoje: 5 min; mês passado: 30 min).
const MONTH_WINDOW = 3;
const MONTHLY_OPEN_MONTH_TTL_MS = 5 * 60 * 1000;
const MONTHLY_CLOSED_MONTH_TTL_MS = 30 * 60 * 1000;
const MONTHLY_FETCH_CONCURRENCY = 4;
const MONTHLY_CACHE_MAX_ENTRIES = 120;
const monthlyProductionCache = new Map();
const monthlyProductionInFlight = new Set();

function buildComparisonMonths(endIso, count, todayIso) {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(String(endIso || ""));
  if (!match) return [];
  const endYear = Number(match[1]);
  const endMonth = Number(match[2]) - 1;
  const months = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const first = new Date(endYear, endMonth - offset, 1);
    const last = new Date(endYear, endMonth - offset + 1, 0);
    const monthKey = `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, "0")}`;
    const lastIso = `${monthKey}-${String(last.getDate()).padStart(2, "0")}`;
    const dataFim = offset === 0 && endIso < lastIso ? endIso : lastIso;
    const shortMonth = first.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
    months.push({
      key: monthKey,
      label: `${shortMonth.charAt(0).toUpperCase()}${shortMonth.slice(1)}/${String(first.getFullYear()).slice(-2)}`,
      dataInicio: `${monthKey}-01`,
      dataFim,
      partial: dataFim < lastIso,
      open: dataFim >= todayIso,
    });
  }
  return months;
}

// Busca sem acento e sem diferença de maiúsculas: "joao" encontra "JOÃO".
function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim();
}

function monthlyCacheKey(baseKey, month) {
  return `${baseKey}|${month.dataInicio}|${month.dataFim}`;
}

function storeMonthlyProduction(key, items) {
  monthlyProductionCache.delete(key);
  monthlyProductionCache.set(key, { items, fetchedAt: Date.now() });
  while (monthlyProductionCache.size > MONTHLY_CACHE_MAX_ENTRIES) {
    monthlyProductionCache.delete(monthlyProductionCache.keys().next().value);
  }
}

async function runWithConcurrency(tasks, limit) {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (next < tasks.length) {
      const task = tasks[next];
      next += 1;
      await task();
    }
  });
  await Promise.all(workers);
}

// null = sem base de comparação; Infinity = parceiro que não produziu no mês anterior.
function computeVariation(current, previous) {
  if (!previous) return current > 0 ? Infinity : null;
  return ((current - previous) / previous) * 100;
}

function formatDayMonth(iso) {
  const [, month, day] = String(iso || "").split("-");
  return `${day}/${month}`;
}

// Variação como tag de status (mesmo StatusBadge sólido do sistema): alta = sucesso, queda = erro,
// estável = neutro, parceiro sem produção no mês anterior = informação.
function VariationValue({ value }) {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  if (value === Infinity) return <StatusBadge status="novo" variant="info" />;
  const rounded = Math.round(value);
  return (
    <StatusBadge
      status={`${rounded > 0 ? "+" : ""}${rounded.toLocaleString("pt-BR")}%`}
      variant={rounded > 0 ? "success" : rounded < 0 ? "danger" : "default"}
      className="tabular-nums"
    />
  );
}

function SortableHeaderCell({ label, sublabel, sortKey, sort, onSort, align = "right", className }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      className={cn(
        "sticky top-0 z-20 whitespace-nowrap border-b border-border bg-card px-3 py-2 font-medium text-muted-foreground",
        align === "right" ? "text-right" : "text-left",
        className
      )}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md text-xs uppercase tracking-wide transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          align === "right" && "flex-row-reverse",
          active && "text-foreground"
        )}
      >
        <span className={cn("flex flex-col", align === "right" ? "items-end" : "items-start")}>
          <span>{label}</span>
          {sublabel ? <span className="text-[10px] normal-case tracking-normal text-muted-foreground">{sublabel}</span> : null}
        </span>
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </button>
    </th>
  );
}

function MonthlyPartnerComparisonCard({
  enabled,
  cacheScope,
  requestBase,
  endDate,
  refreshSignal,
  parceiroByJoinId,
  responsavelById,
  responsavelBySupervisorId,
  onOpenPartner,
}) {
  const [metric, setMetric] = useState("BRUTO");
  const [sort, setSort] = useState({ key: "total", direction: "desc" });
  const [search, setSearch] = useState("");
  const [cacheVersion, setCacheVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestSequenceRef = useRef(0);

  const todayIso = getTodayIso();
  const months = useMemo(() => buildComparisonMonths(endDate, MONTH_WINDOW, todayIso), [endDate, todayIso]);
  const baseKey = useMemo(() => JSON.stringify({ cacheScope, ...requestBase }), [cacheScope, requestBase]);

  useEffect(() => {
    if (!enabled || !months.length) return;
    const now = Date.now();
    const pending = months.filter((month) => {
      const key = monthlyCacheKey(baseKey, month);
      if (monthlyProductionInFlight.has(key)) return false;
      const cached = monthlyProductionCache.get(key);
      if (!cached) return true;
      return now - cached.fetchedAt > (month.open ? MONTHLY_OPEN_MONTH_TTL_MS : MONTHLY_CLOSED_MONTH_TTL_MS);
    });
    if (!pending.length) return;

    const sequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = sequence;
    setLoading(true);
    setError("");
    let failure = null;

    const tasks = pending.map((month) => async () => {
      const key = monthlyCacheKey(baseKey, month);
      monthlyProductionInFlight.add(key);
      try {
        const response = await buscarProducaoParceiros({
          ...requestBase,
          dataInicio: month.dataInicio,
          dataFim: month.dataFim,
        });
        storeMonthlyProduction(key, normalizeRankingParceiros({ rankingParceiros: response?.parceiros || [] }));
        setCacheVersion((version) => version + 1);
      } catch (requestError) {
        failure = failure || requestError;
      } finally {
        monthlyProductionInFlight.delete(key);
      }
    });

    runWithConcurrency(tasks, MONTHLY_FETCH_CONCURRENCY).then(() => {
      if (requestSequenceRef.current !== sequence) return;
      setLoading(false);
      if (failure) {
        setError(failure?.response?.data?.message || "Não foi possível consultar a produção mês a mês.");
      }
    });
  }, [enabled, baseKey, requestBase, months, refreshSignal]);

  // O cache é um Map fora do React; cacheVersion muda a cada mês gravado e força o recálculo.
  const table = useMemo(() => {
    const merged = new Map();
    let missing = false;
    months.forEach((month, monthIndex) => {
      const cached = cacheVersion >= 0 ? monthlyProductionCache.get(monthlyCacheKey(baseKey, month)) : null;
      if (!cached) {
        missing = true;
        return;
      }
      cached.items.forEach((item) => {
        const idJoin = Number(item?.idJoin);
        const hasJoinId = Number.isFinite(idJoin) && idJoin > 0;
        const key = hasJoinId ? `id:${idJoin}` : `nome:${String(item?.label || "").trim().toLocaleLowerCase("pt-BR")}`;
        let row = merged.get(key);
        if (!row) {
          const partner = hasJoinId ? parceiroByJoinId.get(idJoin) : null;
          // Mesma regra do ranking de parceiros: dono do cadastro; sem cadastro, o dono do supervisor Join.
          const responsavel = partner?.usuarioId
            ? responsavelById.get(Number(partner.usuarioId))
            : item?.supervisorId
              ? responsavelBySupervisorId.get(Number(item.supervisorId))
              : null;
          row = {
            key,
            partnerId: partner?.id || null,
            name: partner?.nome || item.label || "Parceiro sem nome",
            responsavel: responsavel || null,
            values: Array(months.length).fill(0),
          };
          merged.set(key, row);
        }
        row.values[monthIndex] += getPartnerRankingMetricValue(item, metric);
      });
    });

    // Mês parcial (a data final do filtro corta o mês) fica fora da média e da variação: comparar
    // 15 dias de setembro com agosto inteiro mostraria todo mundo caindo pela metade.
    const fullIndexes = months.map((month, index) => (month.partial ? -1 : index)).filter((index) => index >= 0);
    const averageIndexes = fullIndexes.length ? fullIndexes : months.map((_, index) => index);
    const variationPair = fullIndexes.length >= 2 ? fullIndexes.slice(-2) : null;
    const summarize = (values) => ({
      total: values.reduce((sum, value) => sum + value, 0),
      average: averageIndexes.length ? averageIndexes.reduce((sum, index) => sum + values[index], 0) / averageIndexes.length : 0,
      variation: variationPair ? computeVariation(values[variationPair[1]], values[variationPair[0]]) : null,
    });

    const rows = Array.from(merged.values()).map((row) => ({ ...row, ...summarize(row.values) }));
    // O total é calculado depois da busca (ver `footer`), por isso sai daqui só a regra de resumo.
    return { rows, summarize, missing, variationPair, hasPartial: fullIndexes.length < months.length };
  }, [months, baseKey, metric, parceiroByJoinId, responsavelById, responsavelBySupervisorId, cacheVersion]);

  // Busca por nome do parceiro ou do responsável. A linha de total acompanha o resultado da busca.
  const normalizedSearch = normalizeSearchText(search);
  const sortedRows = useMemo(() => {
    const factor = sort.direction === "asc" ? 1 : -1;
    const valueOf = (row) => {
      if (sort.key.startsWith("month:")) return row.values[Number(sort.key.slice(6))] ?? 0;
      return row[sort.key];
    };
    const visibleRows = normalizedSearch
      ? table.rows.filter((row) => normalizeSearchText(`${row.name} ${row.responsavel?.nome || ""}`).includes(normalizedSearch))
      : table.rows;
    return [...visibleRows].sort((left, right) => {
      if (sort.key === "name") return factor * left.name.localeCompare(right.name, "pt-BR");
      const leftValue = valueOf(left);
      const rightValue = valueOf(right);
      // Sem base de comparação vai sempre para o fim, nos dois sentidos.
      if (leftValue === null || rightValue === null) {
        if (leftValue === rightValue) return left.name.localeCompare(right.name, "pt-BR");
        return leftValue === null ? 1 : -1;
      }
      if (leftValue === rightValue) return left.name.localeCompare(right.name, "pt-BR");
      return leftValue > rightValue ? factor : -factor;
    });
  }, [table.rows, sort, normalizedSearch]);

  const footer = useMemo(() => {
    const totals = months.map((_, index) => sortedRows.reduce((sum, row) => sum + row.values[index], 0));
    return { values: totals, ...table.summarize(totals) };
  }, [months, sortedRows, table]);

  const handleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "name" ? "asc" : "desc" }
    );
  };

  const formatValue = (value) => {
    if (!value) return <span className="text-muted-foreground">—</span>;
    return metric === "CONTRATOS"
      ? formatNumber(value)
      : Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
  };

  const chipClassName = (active) =>
    cn(
      "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
      active ? "border-transparent bg-foreground text-background" : "border-border bg-background/50 text-muted-foreground hover:text-foreground"
    );

  const metricSelector = (
    <div key="metrica" className="flex flex-wrap gap-1.5" role="group" aria-label="Métrica da comparação">
      {PARTNER_RANKING_OPTIONS.map((option) => (
        <button key={option.key} type="button" aria-pressed={option.key === metric} onClick={() => setMetric(option.key)} className={chipClassName(option.key === metric)}>
          {option.label}
        </button>
      ))}
    </div>
  );

  const searchField = (
    <SearchBar
      key="busca"
      placeholder="Buscar parceiro ou responsável"
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      className="w-full sm:w-72"
    />
  );

  const lastMonth = months[months.length - 1];
  const periodLabel = months.length ? `${months[0].label} a ${lastMonth.label}` : "Sem período";
  const cellClassName = "whitespace-nowrap border-b border-border px-3 py-2 text-right tabular-nums text-foreground transition-colors group-hover:bg-accent";
  // Linha de total: só fundo diferente (opaco, porque fica presa no pé do scroll) e negrito. Fio
  // amarelo e sombra foram testados e recusados pelo dono.
  const footerCellClassName =
    "sticky bottom-0 z-20 whitespace-nowrap border-t border-border bg-muted px-3 py-3 text-right text-sm font-bold tabular-nums text-foreground";
  const { variationPair } = table;

  let content;
  if (table.missing && !error) {
    content = (
      <div className="space-y-2" aria-hidden="true">
        {Array.from({ length: 8 }, (_, index) => (
          <div key={index} className="h-9 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  } else if (!sortedRows.length) {
    content = (
      <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {error ||
          (normalizedSearch && table.rows.length
            ? `Nenhum parceiro ou responsável encontrado para "${search.trim()}".`
            : "Nenhum parceiro com produção nos meses selecionados.")}
      </div>
    );
  } else {
    content = (
      <div className="max-h-[480px] overflow-auto rounded-lg border border-border">
        <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <SortableHeaderCell label="Parceiro" sortKey="name" sort={sort} onSort={handleSort} align="left" className="left-0 z-30 min-w-[260px]" />
              {months.map((month, index) => (
                <SortableHeaderCell
                  key={month.key}
                  label={month.label}
                  sublabel={month.partial ? `até ${formatDayMonth(month.dataFim)}` : null}
                  sortKey={`month:${index}`}
                  sort={sort}
                  onSort={handleSort}
                />
              ))}
              <SortableHeaderCell label="Média" sublabel={table.hasPartial ? "meses fechados" : null} sortKey="average" sort={sort} onSort={handleSort} />
              <SortableHeaderCell label="Total" sortKey="total" sort={sort} onSort={handleSort} />
              {variationPair ? (
                <SortableHeaderCell
                  label="Variação"
                  sublabel={`${months[variationPair[1]].label} × ${months[variationPair[0]].label}`}
                  sortKey="variation"
                  sort={sort}
                  onSort={handleSort}
                />
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.key} className="group">
                <td className="sticky left-0 z-10 max-w-[300px] border-b border-border bg-card px-3 py-2 transition-colors group-hover:bg-accent">
                  {row.partnerId ? (
                    <button
                      type="button"
                      onClick={() => onOpenPartner(row.partnerId)}
                      className="block max-w-full truncate rounded-sm text-left font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {row.name}
                    </button>
                  ) : (
                    <span className="block truncate font-medium text-foreground" title="Parceiro sem cadastro vinculado pelo ID Join">
                      {row.name}
                    </span>
                  )}
                  {row.responsavel ? (
                    <span className="mt-1 flex min-w-0 items-center gap-1.5">
                      <Avatar className="h-5 w-5 shrink-0 rounded-full border border-border">
                        <AvatarImage src={row.responsavel.fotoPerfilUrl} alt={row.responsavel.nome} />
                        <AvatarFallback className="bg-muted text-[9px] text-muted-foreground">
                          {getAvatarInitials(row.responsavel.nome)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate text-xs text-muted-foreground">{row.responsavel.nome}</span>
                    </span>
                  ) : (
                    <span className="mt-1 block text-xs text-muted-foreground">Responsável não relacionado</span>
                  )}
                </td>
                {row.values.map((value, index) => (
                  <td key={months[index].key} className={cellClassName}>{formatValue(value)}</td>
                ))}
                <td className={cellClassName}>{formatValue(row.average)}</td>
                <td className={cn(cellClassName, "font-semibold")}>{formatValue(row.total)}</td>
                {variationPair ? (
                  <td className={cellClassName}><VariationValue value={row.variation} /></td>
                ) : null}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className={cn(footerCellClassName, "left-0 z-30 text-left")}>
                <span className="block text-xs font-bold uppercase tracking-[0.16em] text-foreground">Total</span>
                <span className="block text-[11px] font-medium normal-case tracking-normal text-muted-foreground">
                  {formatNumber(sortedRows.length)} parceiros
                </span>
              </td>
              {footer.values.map((value, index) => (
                <td key={months[index].key} className={footerCellClassName}>{formatValue(value)}</td>
              ))}
              <td className={footerCellClassName}>{formatValue(footer.average)}</td>
              <td className={cn(footerCellClassName, "text-base")}>{formatValue(footer.total)}</td>
              {variationPair ? (
                <td className={footerCellClassName}><VariationValue value={footer.variation} /></td>
              ) : null}
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  return (
    <Card title="Parceiros mês a mês" actions={[searchField, metricSelector]}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <p>
            {periodLabel} · filtros gerais aplicados
            {lastMonth?.partial ? ` · ${lastMonth.label} até ${formatDayMonth(lastMonth.dataFim)}, fora da média e da variação` : ""}
          </p>
          <p className="flex items-center gap-2 tabular-nums">
            {loading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-label="Atualizando" /> : null}
            {table.missing ? "" : `${formatNumber(sortedRows.length)} parceiros`}
          </p>
        </div>
        {error && sortedRows.length ? <p className="text-sm text-destructive">{error}</p> : null}
        {content}
      </div>
    </Card>
  );
}

// Campanha e linha de ranking se encontram pelo NOME do banco/convenio: o ranking normalizado nem
// sempre traz o id, e o nome e o que a Join devolve nos dois lugares.
function chaveCampanha(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

function RankingBarCard({
  title,
  subtitle,
  items = [],
  color,
  formatter = formatCurrency,
  emptyLabel = "Nenhum ranking disponível para o período.",
  rankingMode,
  onRankingModeChange,
  rankingOptions = PARTNER_RANKING_OPTIONS,
  campanhasPorNome,
}) {
  const [page, setPage] = useState(1);
  
  useEffect(() => {
    setPage(1);
  }, [rankingMode]);

  const itensComCampanha = useMemo(() => {
    if (!campanhasPorNome?.size) return items;
    const emCampanha = [];
    const demais = [];
    items.forEach((item) => {
      (campanhasPorNome.has(chaveCampanha(item.label)) ? emCampanha : demais).push(item);
    });
    // Banco em campanha que nao produziu no periodo filtrado nao vem no ranking; mesmo assim ele
    // precisa aparecer, senao a campanha some da tela justamente quando esta indo mal.
    const jaListados = new Set(items.map((item) => chaveCampanha(item.label)));
    campanhasPorNome.forEach((campanha, chave) => {
      if (!jaListados.has(chave)) {
        emCampanha.push({ label: campanha.nomeReferencia || campanha.rotulo, value: 0, semProducao: true });
      }
    });
    return [...emCampanha, ...demais];
  }, [items, campanhasPorNome]);

  const max = Math.max(...itensComCampanha.map((item) => Number(item.value || 0)), 0);
  const totalPages = Math.max(1, Math.ceil(itensComCampanha.length / RANKING_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageOffset = (currentPage - 1) * RANKING_PAGE_SIZE;
  const paginatedItems = itensComCampanha.slice(pageOffset, pageOffset + RANKING_PAGE_SIZE);
  const emptySlots = totalPages > 1 ? RANKING_PAGE_SIZE - paginatedItems.length : 0;

  const rankingSelector = onRankingModeChange ? (
    <select
      className="h-9 rounded-full border border-border/70 bg-background/40 px-3 text-xs font-medium text-foreground shadow-sm outline-none transition focus:border-primary/50 focus:ring-2 focus:ring-ring/30"
      aria-label="Metrica do ranking"
      value={rankingMode || "BRUTO"}
      onChange={(event) => onRankingModeChange(event.target.value)}
    >
      {rankingOptions.map((option) => (
        <option key={option.key} value={option.key}>
          {option.label}
        </option>
      ))}
    </select>
  ) : null;

  return (
    <Card title={title} actions={rankingSelector ? [rankingSelector] : undefined}>
      <div className="space-y-4">
        {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
        {items.length ? (
          <div className="space-y-3">
            {paginatedItems.map((item, index) => {
              const campanha = campanhasPorNome?.get(chaveCampanha(item.label));
              // O destaque começa na PRÓPRIA linha do banco: como bloco separado embaixo, parecia
              // um aviso solto. A cor vem da campanha, que guarda a cor da marca do banco.
              return (
                <div key={`${item.label}-${index}`} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="w-7 shrink-0 text-right text-xs font-semibold tabular-nums text-muted-foreground">
                        {pageOffset + index + 1}º
                      </span>
                      {campanha ? <CampanhaIcone campanha={campanha} /> : null}
                      <span className="truncate font-medium text-foreground">{item.label}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {item.semProducao ? "sem produção no período" : formatter(item.value)}
                    </span>
                  </div>
                  <CampanhaMetaBar campanha={campanha} />
                </div>
              );
            })}
            {Array.from({ length: emptySlots }, (_, index) => (
              <div key={`vazio-${index}`} className="invisible space-y-1.5" aria-hidden="true">
                <div className="text-sm">&nbsp;</div>
                <div className="h-2.5" />
              </div>
            ))}
            <Pagination
              page={currentPage}
              pageSize={RANKING_PAGE_SIZE}
              totalItems={itensComCampanha.length}
              onPageChange={setPage}
            />
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        )}
      </div>
    </Card>
  );
}

function SessionUserRow({ user }) {
  const online = Boolean(user?.sessaoAtiva);

  return (
    <div className="flex flex-col gap-3 rounded-3xl border border-border/70 bg-background/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar className="h-11 w-11 rounded-2xl">
          <AvatarImage src={user?.fotoPerfilUrl} alt={user?.nome} />
          <AvatarFallback className="bg-muted text-foreground">
            {getAvatarInitials(user?.nome)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{user?.nome || "Usuário"}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email || "Sem email"}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 sm:block sm:text-right">
        <StatusBadge status={online ? "Online" : "Offline"} variant={online ? "success" : "danger"} />
        <p className="mt-1 text-xs text-muted-foreground">
          {online ? `Logado ha ${formatElapsedSeconds(user?.tempoLogadoSegundos)}` : `Ultimo logout ${formatDateTime(user?.ultimoLogoutEm)}`}
        </p>
      </div>
    </div>
  );
}

export default function DashboardGestaoComercial() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => loadFilters());
  const [usuario, setUsuario] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [activityUsers, setActivityUsers] = useState(null);
  const [parceiros, setParceiros] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(DASHBOARD_AUTO_REFRESH_SECONDS);
  const [visibleAreaMetrics, setVisibleAreaMetrics] = useState([
    "producaoBruta",
    "producaoLiquida",
    "producaoComissionada",
  ]);
  const [partnerRankingMode, setPartnerRankingMode] = useState("BRUTO");
  const [bankRankingMode, setBankRankingMode] = useState("BRUTO");
  const loadingDashboardRef = useRef(false);
  const dashboardRequestSequenceRef = useRef(0);
  const refreshActionRef = useRef(null);
  const [sessaoRelatorioRange, setSessaoRelatorioRange] = useState(getDefaultSessaoRelatorioRange);
  const [sessaoRelatorio, setSessaoRelatorio] = useState(null);
  // Pedido de recarga do relatório de sessões vindo do timer ou do botão de atualizar: na aba
  // Equipe é ele que faz o papel do carregarDashboard das outras abas.
  const [sessaoRelatorioTick, setSessaoRelatorioTick] = useState(0);
  const [loadingSessaoRelatorio, setLoadingSessaoRelatorio] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = DASHBOARD_PAGE_TABS.some((tab) => tab.value === searchParams.get("aba"))
    ? searchParams.get("aba")
    : "vendas";
  // Blocos que esta aba precisa do /gestao-comercial. Equipe não usa esse endpoint: a lista vazia
  // faz a consulta ser pulada inteira.
  const secoesDaAba = DASHBOARD_TAB_SECOES[activeTab] || [];

  const isAdmin = isAdminUser(usuario);
  const todayIso = getTodayIso();

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    if (bankRankingMode !== "CONTRATOS") return;
    setFilters((prev) => (prev.tipoPeriodo === "CADASTRO" ? prev : { ...prev, tipoPeriodo: "CADASTRO" }));
  }, [bankRankingMode]);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      setLoading(true);
      try {
        const user = await getUsuarioLogado();
        const admin = isAdminUser(user);
        const [bancosAtivos, conveniosAtivos, parceirosAtivos, usuariosAtivos] = await Promise.all([
          listarBancosAtivos(),
          listarConveniosAtivos(),
          listarParceiros(),
          admin ? listarTodosUsuariosAtivos() : Promise.resolve(buildScopedUserList(user)),
        ]);

        if (!mounted) return;
        setUsuario(user);
        setBancos(bancosAtivos);
        setConvenios(conveniosAtivos);
        setResponsaveis(usuariosAtivos);
        setActivityUsers(usuariosAtivos);
        setParceiros(parceirosAtivos);
      } catch (error) {
        if (!mounted) return;
        SwalWithTheme.error("Erro", error?.response?.data?.message || "Não foi possível carregar o dashboard");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    bootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (loading || !usuario || !isAdmin) {
      setActivityUsers(buildScopedUserList(usuario));
      return undefined;
    }

    let cancelled = false;
    let refreshing = false;

    const refreshActivityUsers = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const usuariosAtivos = await listarTodosUsuariosAtivos({ suppressActivityTracking: true });
        if (!cancelled) {
          setActivityUsers(Array.isArray(usuariosAtivos) ? usuariosAtivos : []);
        }
      } catch {
        // polling silencioso para nao interferir no dashboard principal
      } finally {
        refreshing = false;
      }
    };

    const intervalId = window.setInterval(refreshActivityUsers, DASHBOARD_AUTO_REFRESH_SECONDS * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isAdmin, loading, usuario]);

  useEffect(() => {
    // Fora da aba Equipe ninguém lê este relatório: antes ele era buscado em toda visita ao
    // dashboard, inclusive por quem só ia olhar Vendas.
    if (activeTab !== "equipe" || !isAdmin || loading) {
      setSessaoRelatorio(null);
      return undefined;
    }

    const from = sessaoRelatorioRange?.from || "";
    const to = sessaoRelatorioRange?.to || "";
    if (!from || !to) {
      setSessaoRelatorio(null);
      return undefined;
    }

    let cancelled = false;
    const usuarioIds = filters.responsavelIds?.length ? filters.responsavelIds.map(Number) : undefined;

    async function loadReport() {
      setLoadingSessaoRelatorio(true);
      try {
        const resp = await consultarRelatorioSessoes({
          dataInicio: from,
          dataFim: to,
          usuarioIds,
          suppressActivityTracking: true,
        });
        if (!cancelled) {
          setSessaoRelatorio(resp || null);
        }
      } catch {
        if (!cancelled) setSessaoRelatorio(null);
      } finally {
        if (!cancelled) setLoadingSessaoRelatorio(false);
      }
    }

    loadReport();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    filters.responsavelIds,
    isAdmin,
    loading,
    sessaoRelatorioRange?.from,
    sessaoRelatorioRange?.to,
    sessaoRelatorioTick,
  ]);

  const tipoProdutoOptions = useMemo(() => {
    const map = new Map();
    (convenios || []).forEach((convenio) => {
      (convenio?.tiposConvenio || []).forEach((tipo) => {
        const codigo = String(tipo?.codigo || "").trim().toUpperCase();
        if (!codigo || map.has(codigo)) return;
        map.set(codigo, {
          value: codigo,
          label: String(tipo?.nome || codigo).trim() || codigo,
          ordem: Number.isFinite(Number(tipo?.ordem)) ? Number(tipo.ordem) : TYPE_ORDER[codigo] ?? 999,
        });
      });
    });

    const options = Array.from(map.values())
      .sort((left, right) => left.ordem - right.ordem || left.label.localeCompare(right.label))
      .map(({ ordem, ...option }) => option);
    if (options.length) return options;
    return [
      { value: "NOVO", label: "Contrato Novo", ordem: TYPE_ORDER.NOVO ?? 0 },
      { value: "PORT", label: "Portabilidade", ordem: TYPE_ORDER.PORT ?? 1 },
      { value: "REFIN", label: "Refinanciamento", ordem: TYPE_ORDER.REFIN ?? 2 },
    ]
      .sort((left, right) => left.ordem - right.ordem)
      .map(({ ordem, ...option }) => option);
  }, [convenios]);

  const supervisorOptions = useMemo(() => {
    const map = new Map();
    responsaveis.forEach(user => {
       if (user.supervisorIds) {
           const ids = String(user.supervisorIds).split(",").map(s => s.trim()).filter(Boolean);
           if (ids.length) {
               ids.forEach(id => map.set(id, { value: id, label: user.nome }));
           }
       }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [responsaveis]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      tiposConvenio: prev.tiposConvenio.filter((value) => tipoProdutoOptions.some((option) => option.value === value)),
    }));
  }, [tipoProdutoOptions]);

  useEffect(() => {
    loadingDashboardRef.current = loadingDashboard;
  }, [loadingDashboard]);

  const dashboardRequestKey = useMemo(
    () =>
      JSON.stringify({
        secoes: secoesDaAba,
        responsavelIds: isAdmin ? filters.responsavelIds.map(Number).sort((left, right) => left - right) : [],
        supervisorId: filters.supervisorIds ? filters.supervisorIds.flatMap(v => String(v).split(',')).map(Number).filter(Boolean).sort((left, right) => left - right) : [],
        dataInicio: filters.dataInicio,
        dataFim: filters.dataFim,
        bancosIds: filters.bancosIds.map(Number).sort((left, right) => left - right),
        conveniosIds: filters.conveniosIds.map(Number).sort((left, right) => left - right),
        tiposConvenio: filters.tiposConvenio,
        tipoPeriodo: filters.tipoPeriodo,
      }),
    [
      secoesDaAba,
      isAdmin,
      filters.responsavelIds,
      filters.supervisorIds,
      filters.dataInicio,
      filters.dataFim,
      filters.bancosIds,
      filters.conveniosIds,
      filters.tiposConvenio,
      filters.tipoPeriodo,
    ]
  );

  // Mesmos filtros da consulta do dashboard, sem o período: o card mês a mês monta os meses a partir
  // da data final.
  const monthlyComparisonFilters = useMemo(() => {
    const { dataInicio, dataFim, secoes, ...rest } = JSON.parse(dashboardRequestKey);
    return rest;
  }, [dashboardRequestKey]);

  async function carregarDashboard({ resetTimer = false } = {}) {
    if (!usuario) return;

    if (resetTimer) {
      setRefreshCountdown(DASHBOARD_AUTO_REFRESH_SECONDS);
    }

    // A aba Equipe não consome este endpoint: ela tem o relatório de sessões, que o efeito abaixo
    // recarrega quando o contador vira. Sem isto o timer buscaria produção e tarefas à toa.
    if (!secoesDaAba.length) {
      setSessaoRelatorioTick((current) => current + 1);
      return;
    }

    const requestSequence = dashboardRequestSequenceRef.current + 1;
    dashboardRequestSequenceRef.current = requestSequence;

    setLoadingDashboard(true);
    try {
      const data = await buscarDashboardGestaoComercial({
        responsavelIds: isAdmin ? filters.responsavelIds.map(Number) : [],
        supervisorId: filters.supervisorIds ? filters.supervisorIds.flatMap(v => String(v).split(',')).map(Number).filter(Boolean) : [],
        dataInicio: filters.dataInicio,
        dataFim: filters.dataFim,
        bancosIds: filters.bancosIds.map(Number),
        conveniosIds: filters.conveniosIds.map(Number),
        tiposConvenio: filters.tiposConvenio,
        tipoPeriodo: filters.tipoPeriodo,
        granularidadeEvolucao: "DIA",
        secoes: secoesDaAba,
      });
      if (dashboardRequestSequenceRef.current !== requestSequence) {
        return;
      }
      // O servidor devolve nulo no bloco que não foi pedido. Preservar o bloco anterior mantém a
      // outra aba preenchida ao voltar para ela, em vez de piscar zerada até a resposta chegar.
      setDashboard((prev) => mergeDashboardSections(prev, data));
    } catch (error) {
      if (dashboardRequestSequenceRef.current !== requestSequence) {
        return;
      }
      SwalWithTheme.error("Erro", error?.response?.data?.message || "Não foi possível atualizar o dashboard");
    } finally {
      if (dashboardRequestSequenceRef.current === requestSequence) {
        setLoadingDashboard(false);
      }
    }
  }

  useEffect(() => {
    refreshActionRef.current = carregarDashboard;
  });

  useEffect(() => {
    if (!usuario) {
      clearDashboardRefreshState();
      return undefined;
    }

    const handleRefreshRequest = (event) => {
      refreshActionRef.current?.(event?.detail || {});
    };

    window.addEventListener(DASHBOARD_REFRESH_REQUEST_EVENT, handleRefreshRequest);
    return () => {
      window.removeEventListener(DASHBOARD_REFRESH_REQUEST_EVENT, handleRefreshRequest);
      clearDashboardRefreshState();
    };
  }, [usuario]);

  useEffect(() => {
    if (!usuario) return;
    // Aba sem seção (Equipe) é carregada pelo efeito do relatório de sessões, que reage à aba e aos
    // filtros por conta própria. Chamar o carregador aqui só duplicaria a consulta.
    if (!secoesDaAba.length) {
      setRefreshCountdown(DASHBOARD_AUTO_REFRESH_SECONDS);
      return;
    }
    refreshActionRef.current?.({ resetTimer: true });
  }, [usuario, dashboardRequestKey, secoesDaAba.length]);

  useEffect(() => {
    if (!usuario) return undefined;

    setRefreshCountdown(DASHBOARD_AUTO_REFRESH_SECONDS);

    const intervalId = window.setInterval(() => {
      setRefreshCountdown((current) => {
        if (current <= 1) {
          if (loadingDashboardRef.current || !refreshActionRef.current) {
            return 1;
          }
          refreshActionRef.current({ resetTimer: true });
          return DASHBOARD_AUTO_REFRESH_SECONDS;
        }
        return current - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [usuario]);

  useEffect(() => {
    if (!usuario) {
      clearDashboardRefreshState();
      return;
    }

    publishDashboardRefreshState({
      visible: true,
      // Na aba Equipe quem carrega é o relatório de sessões: o indicador da barra superior tem de
      // girar por ele, senão a atualização parece não acontecer.
      loading: secoesDaAba.length ? loadingDashboard : loadingSessaoRelatorio,
      countdown: refreshCountdown,
      countdownLabel: formatRefreshCountdown(refreshCountdown),
    });
  }, [loadingDashboard, loadingSessaoRelatorio, refreshCountdown, secoesDaAba.length, usuario]);

  const producaoResumo = useMemo(() => normalizeDashboardResumo(dashboard?.producao || {}), [dashboard]);
  const statusContratosResumo = useMemo(() => normalizeDashboardContractStatus(dashboard?.producao || {}), [dashboard]);
  const producaoEvolucao = useMemo(() => normalizeDashboardEvolucao(dashboard?.producao || {}), [dashboard]);

  const granularidadeEvolucao = dashboard?.escopo?.granularidadeEvolucao || "DIA";
  const producaoGranularidade = "DIA";
  const producaoEvolucaoCompleta = useMemo(
    () => buildContinuousTimeline(producaoEvolucao, filters.dataInicio, filters.dataFim, producaoGranularidade),
    [producaoEvolucao, filters.dataInicio, filters.dataFim, producaoGranularidade]
  );
  const contratosStatusEvolucao = useMemo(
    () =>
      producaoEvolucaoCompleta.map((point) => ({
        data: point?.data || "",
        contratosAprovados: Number(point?.contratosAprovados || 0),
        contratosCancelados: Number(point?.contratosCancelados || 0),
        contratosNegados: Number(point?.contratosNegados || 0),
        contratosEmAndamento: Number(point?.contratosEmAndamento || 0),
      })),
    [producaoEvolucaoCompleta]
  );
  const rankingParceiros = useMemo(() => normalizeRankingParceiros(dashboard?.producao || {}), [dashboard]);
  const rankingBancos = useMemo(() => normalizeRankingBancos(dashboard?.producao || {}, bankRankingMode), [dashboard, bankRankingMode]);
  const [campanhasVigentes, setCampanhasVigentes] = useState([]);
  useEffect(() => {
    let ativo = true;
    listarCampanhasVigentes()
      .then((lista) => {
        if (ativo) setCampanhasVigentes(lista);
      })
      .catch(() => {
        if (ativo) setCampanhasVigentes([]);
      });
    return () => {
      ativo = false;
    };
  }, []);
  const rankingConvenios = useMemo(() => normalizeRankingConvenios(dashboard?.producao || {}, bankRankingMode), [dashboard, bankRankingMode]);
  const rankingMetricLabel = useMemo(
    () => PARTNER_RANKING_OPTIONS.find((option) => option.key === partnerRankingMode)?.label || "Bruto",
    [partnerRankingMode]
  );
  const rankingMetricFormatter = useMemo(() => (partnerRankingMode === "CONTRATOS" ? formatNumber : formatCurrency), [partnerRankingMode]);
  const bankRankingMetricLabel = useMemo(
    () => PARTNER_RANKING_OPTIONS.find((option) => option.key === bankRankingMode)?.label || "Bruto",
    [bankRankingMode]
  );
  const bankRankingMetricFormatter = useMemo(() => (bankRankingMode === "CONTRATOS" ? formatNumber : formatCurrency), [bankRankingMode]);
  const tarefasEvolucaoCompleta = useMemo(
    () => buildContinuousTimeline(dashboard?.tarefasEvolucao || [], filters.dataInicio, filters.dataFim, granularidadeEvolucao),
    [dashboard, filters.dataInicio, filters.dataFim, granularidadeEvolucao]
  );

  const responsavelById = useMemo(() => new Map(responsaveis.map((item) => [Number(item.id), item])), [responsaveis]);
  const responsavelBySupervisorId = useMemo(() => {
    const map = new Map();
    responsaveis.forEach((item) => {
      String(item?.supervisorIds || "")
        .split(",")
        .map((entry) => Number(String(entry || "").trim()))
        .filter((entry) => Number.isFinite(entry) && entry > 0)
        .forEach((entry) => {
          if (!map.has(entry)) {
            map.set(entry, item);
          }
        });
    });
    return map;
  }, [responsaveis]);
  const parceiroByJoinId = useMemo(() => {
    const map = new Map();
    parceiros.forEach((item) => {
      const joinId = Number(item?.idJoin);
      if (Number.isFinite(joinId) && joinId > 0) {
        map.set(joinId, item);
      }
    });
    return map;
  }, [parceiros]);

  const enrichedRankingParceiros = useMemo(
    () =>
      rankingParceiros
        .map((item) => {
          const partner = item.idJoin ? parceiroByJoinId.get(Number(item.idJoin)) : null;
          const responsavel = partner?.usuarioId
            ? responsavelById.get(Number(partner.usuarioId))
            : item.supervisorId
              ? responsavelBySupervisorId.get(Number(item.supervisorId))
              : null;

          return {
            ...item,
            rankingValue: getPartnerRankingMetricValue(item, partnerRankingMode),
            partnerId: partner?.id || null,
            partnerName: partner?.nome || item.label,
            responsavel,
          };
        })
        .sort((left, right) => right.rankingValue - left.rankingValue),
    [rankingParceiros, parceiroByJoinId, responsavelById, responsavelBySupervisorId, partnerRankingMode]
  );

  const producaoCards = useMemo(
    () => [
      {
        icon: CircleDollarSign,
        label: "Produção bruta",
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={producaoResumo.producaoBrutaTotal || 0}
            format={{ style: "currency", currency: "BRL" }}
          />
        ),
        helper: "Total no período",
        accent: "var(--chart-1)",
      },
      {
        icon: TrendingUp,
        label: "Produção líquida",
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={producaoResumo.producaoLiquidaTotal || 0}
            format={{ style: "currency", currency: "BRL" }}
          />
        ),
        helper: "Líquido no período",
        accent: "var(--chart-2)",
      },
      {
        icon: Percent,
        label: "Produção comissionada",
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={producaoResumo.producaoComissionadaTotal || 0}
            format={{ style: "currency", currency: "BRL" }}
          />
        ),
        helper: "Comissão no período",
        accent: "var(--chart-3)",
      },
      {
        icon: Percent,
        label: "Repasse médio",
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={producaoResumo.percentualRepasseMedio || 0}
            format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            suffix="%"
          />
        ),
        helper: "Média no período",
        accent: "var(--chart-4)",
      },
      {
        icon: Activity,
        label: "Spread médio",
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={producaoResumo.spreadMedio || 0}
            format={{ maximumFractionDigits: 2 }}
          />
        ),
        helper: "Média no período",
        accent: "var(--chart-5)",
      },
    ],
    [producaoResumo]
  );
  const toggleAreaMetric = (metricKey) => {
    setVisibleAreaMetrics((current) => {
      const alreadyVisible = current.includes(metricKey);
      if (alreadyVisible) {
        if (current.length === 1) {
          return current;
        }
        return current.filter((key) => key !== metricKey);
      }
      return [...current, metricKey];
    });
  };
  const totalStatusContratos = useMemo(
    () =>
      Number(statusContratosResumo.aprovados || 0) +
      Number(statusContratosResumo.cancelados || 0) +
      Number(statusContratosResumo.negados || 0) +
      Number(statusContratosResumo.emAndamento || 0),
    [statusContratosResumo]
  );
  const contratosStatusCards = useMemo(
    () => [
      {
        icon: BriefcaseBusiness,
        label: "Total digitado",
        value: (
          <NumberFlowValue className="tabular-nums" value={totalStatusContratos || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: "Todos no período",
        // Violeta: o neutro virava um bloco preto no degradê. Não aparece em gráfico, então não confunde.
        accent: "var(--chart-4)",
      },
      {
        icon: CONTRACT_STATUS_META.aprovados.icon,
        label: CONTRACT_STATUS_META.aprovados.label,
        value: (
          <NumberFlowValue className="tabular-nums" value={statusContratosResumo.aprovados || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: CONTRACT_STATUS_META.aprovados.helper,
        accent: CONTRACT_STATUS_META.aprovados.accent,
      },
      {
        icon: CONTRACT_STATUS_META.cancelados.icon,
        label: CONTRACT_STATUS_META.cancelados.label,
        value: (
          <NumberFlowValue className="tabular-nums" value={statusContratosResumo.cancelados || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: CONTRACT_STATUS_META.cancelados.helper,
        accent: CONTRACT_STATUS_META.cancelados.accent,
      },
      {
        icon: CONTRACT_STATUS_META.negados.icon,
        label: CONTRACT_STATUS_META.negados.label,
        value: (
          <NumberFlowValue className="tabular-nums" value={statusContratosResumo.negados || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: CONTRACT_STATUS_META.negados.helper,
        accent: CONTRACT_STATUS_META.negados.accent,
        ink: CONTRACT_STATUS_META.negados.ink,
      },
      {
        icon: CONTRACT_STATUS_META.emAndamento.icon,
        label: CONTRACT_STATUS_META.emAndamento.label,
        value: (
          <NumberFlowValue className="tabular-nums" value={statusContratosResumo.emAndamento || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: CONTRACT_STATUS_META.emAndamento.helper,
        accent: CONTRACT_STATUS_META.emAndamento.accent,
      },
    ],
    [statusContratosResumo, totalStatusContratos]
  );

  const tarefasResumo = dashboard?.tarefasResumo || {};
  const tarefasCards = useMemo(
    () => [
      {
        icon: BriefcaseBusiness,
        label: "Tarefas criadas",
        value: (
          <NumberFlowValue className="tabular-nums" value={tarefasResumo.total || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: "No período",
        accent: STATUS_COLOR.info,
      },
      {
        icon: CheckCircle2,
        label: "Concluidas no prazo",
        value: (
          <NumberFlowValue className="tabular-nums" value={tarefasResumo.concluidas || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: "Dentro do prazo",
        accent: STATUS_COLOR.success,
      },
      {
        icon: XCircle,
        label: "Canceladas",
        value: (
          <NumberFlowValue className="tabular-nums" value={tarefasResumo.canceladas || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: "No período",
        accent: STATUS_COLOR.destructive,
      },
      {
        icon: Clock3,
        label: "Concluidas com atraso",
        value: (
          <NumberFlowValue className="tabular-nums" value={tarefasResumo.concluidasComAtraso || 0} format={{ maximumFractionDigits: 0 }} />
        ),
        helper: "Fechadas com atraso",
        // Amarelo da marca: branco não se lê, então texto escuro.
        accent: STATUS_COLOR.warning,
        ink: "dark",
      },
    ],
    [tarefasResumo]
  );

  const activityUserPool = activityUsers ?? responsaveis;

  const monitoredUsers = useMemo(() => {
    if (!activityUserPool.length) {
      return usuario ? [usuario] : [];
    }

    if (isAdmin) {
      if (filters.responsavelIds.length > 0) {
        return activityUserPool.filter((item) => filters.responsavelIds.includes(String(item.id)));
      }
      return activityUserPool;
    }

    return activityUserPool.filter((item) => Number(item.id) === Number(usuario?.id));
  }, [activityUserPool, usuario, isAdmin, filters.responsavelIds]);

  const onlineUsers = useMemo(() => monitoredUsers.filter((item) => item?.sessaoAtiva), [monitoredUsers]);
  const monitoredUsersSorted = useMemo(
    () => monitoredUsers.slice().sort((left, right) => Number(right?.tempoLogadoSegundos || 0) - Number(left?.tempoLogadoSegundos || 0)),
    [monitoredUsers]
  );
  const monitoredUserColumns = useMemo(
    () =>
      monitoredUsersSorted.length >= 10
        ? [
          monitoredUsersSorted.slice(0, Math.ceil(monitoredUsersSorted.length / 2)),
          monitoredUsersSorted.slice(Math.ceil(monitoredUsersSorted.length / 2)),
        ]
        : [monitoredUsersSorted],
    [monitoredUsersSorted]
  );
  const longestSessionUser = useMemo(
    () =>
      onlineUsers.reduce((best, current) => {
        if (!best) return current;
        return Number(current?.tempoLogadoSegundos || 0) > Number(best?.tempoLogadoSegundos || 0) ? current : best;
      }, null),
    [onlineUsers]
  );

  const averageSessionSeconds = useMemo(() => {
    if (!onlineUsers.length) return 0;
    const total = onlineUsers.reduce((sum, item) => sum + Number(item?.tempoLogadoSegundos || 0), 0);
    return Math.round(total / onlineUsers.length);
  }, [onlineUsers]);

  const statusItems = dashboard?.tarefasPorStatus || [];
  const statusItemsTotal = useMemo(
    () => statusItems.reduce((sum, item) => sum + Number(item?.total || 0), 0),
    [statusItems]
  );
  const tiposTarefaItems = (dashboard?.tarefasPorTipo || []).map((item) => ({ label: item.label, value: item.total }));
  const motivosTarefaItems = (dashboard?.tarefasPorMotivo || []).map((item) => ({ label: item.label, value: item.total }));
  const motivosByTipo = useMemo(() => {
    const groups = Array.isArray(dashboard?.tarefasMotivosPorTipo) ? dashboard.tarefasMotivosPorTipo : [];
    const map = new Map();
    groups.forEach((group) => {
      const label = String(group?.label || "").trim();
      if (!label) return;
      const items = Array.isArray(group?.items)
        ? group.items.map((item) => ({ label: item.label, value: item.total }))
        : [];
      map.set(label, items);
    });
    return map;
  }, [dashboard]);
  const tipoMotivoOptions = useMemo(
    () => [{ value: "__ALL__", label: "Todos os tipos" }, ...tiposTarefaItems.map((item) => ({ value: item.label, label: item.label }))],
    [tiposTarefaItems]
  );
  const [motivoTipoSelecionado, setMotivoTipoSelecionado] = useState("__ALL__");
  useEffect(() => {
    const values = new Set(tipoMotivoOptions.map((option) => String(option.value)));
    if (!values.has(String(motivoTipoSelecionado))) {
      setMotivoTipoSelecionado("__ALL__");
    }
  }, [motivoTipoSelecionado, tipoMotivoOptions]);
  const motivosTarefaItemsFiltrados = useMemo(() => {
    if (motivoTipoSelecionado === "__ALL__") return motivosTarefaItems;
    return motivosByTipo.get(motivoTipoSelecionado) || [];
  }, [motivoTipoSelecionado, motivosByTipo, motivosTarefaItems]);

  const bancoOptions = useMemo(
    () => bancos.map((item) => ({ value: String(item.bancoId), label: item.bancoNome })),
    [bancos]
  );
  const convenioOptions = useMemo(
    () => convenios.map((item) => ({ value: String(item.convenioId), label: item.convenioNome })),
    [convenios]
  );

  // O id da campanha e casado com o nome via lista de bancos/convenios ja carregada para os filtros.
  const campanhasPorNomeBanco = useMemo(() => {
    const mapa = new Map();
    campanhasVigentes.filter((campanha) => campanha.tipo === "BANCO").forEach((campanha) => {
      const nome = bancoOptions.find((opcao) => String(opcao.value) === String(campanha.referenciaId))?.label;
      // nomeReferencia serve para a linha fixada quando o banco nao aparece no ranking.
      if (nome) mapa.set(chaveCampanha(nome), { ...campanha, nomeReferencia: nome });
    });
    return mapa;
  }, [campanhasVigentes, bancoOptions]);
  const campanhasPorNomeConvenio = useMemo(() => {
    const mapa = new Map();
    campanhasVigentes.filter((campanha) => campanha.tipo === "CONVENIO").forEach((campanha) => {
      const nome = convenioOptions.find((opcao) => String(opcao.value) === String(campanha.referenciaId))?.label;
      // nomeReferencia serve para a linha fixada quando o banco nao aparece no ranking.
      if (nome) mapa.set(chaveCampanha(nome), { ...campanha, nomeReferencia: nome });
    });
    return mapa;
  }, [campanhasVigentes, convenioOptions]);

  // Etiquetas dos filtros aplicados na aba atual. Com os filtros num painel fechado, é o único
  // jeito de saber o que está valendo sem abrir o painel.
  const activeFilterChips = useMemo(() => {
    const periodo = `${formatIsoToBr(filters.dataInicio)} – ${formatIsoToBr(filters.dataFim)}`;
    const countChip = (label, values) => (values?.length ? `${label}: ${values.length}` : null);

    if (activeTab === "tarefas") return [periodo];
    if (activeTab === "equipe") {
      return isAdmin
        ? [`Sessões: ${formatIsoToBr(sessaoRelatorioRange.from)} – ${formatIsoToBr(sessaoRelatorioRange.to)}`]
        : [];
    }
    return [
      periodo,
      countChip("Bancos", filters.bancosIds),
      countChip("Convênios", filters.conveniosIds),
      countChip("Tipos de produto", filters.tiposConvenio),
      countChip("Supervisores", filters.supervisorIds),
      `Base: ${TIPO_PERIODO_OPTIONS.find((option) => option.value === filters.tipoPeriodo)?.label || "Aprovação"}`,
    ].filter(Boolean);
  }, [activeTab, filters, isAdmin, sessaoRelatorioRange]);

  // Filtros abrem pelo funil da barra superior, que mostra a contagem (padrão de todas as páginas).
  usePageFilters({ count: activeFilterChips.length, onOpen: () => setFiltersModalOpen(true) });

  // "Limpar filtros" volta ao padrão só o que a aba aberta filtra (o painel mostra só esses campos).
  const tabFiltersAreDefault = useMemo(() => {
    if (activeTab === "equipe") {
      const defaults = getDefaultSessaoRelatorioRange();
      return sessaoRelatorioRange.from === defaults.from && sessaoRelatorioRange.to === defaults.to;
    }
    const defaults = getDefaultFilters();
    const periodIsDefault = filters.dataInicio === defaults.dataInicio && filters.dataFim === defaults.dataFim;
    if (activeTab === "tarefas") return periodIsDefault;
    return (
      periodIsDefault &&
      !filters.bancosIds.length &&
      !filters.conveniosIds.length &&
      !filters.tiposConvenio.length &&
      !filters.supervisorIds.length &&
      filters.tipoPeriodo === defaults.tipoPeriodo
    );
  }, [activeTab, filters, sessaoRelatorioRange]);

  const handleClearFilters = () => {
    if (activeTab === "equipe") {
      setSessaoRelatorioRange(getDefaultSessaoRelatorioRange());
      return;
    }
    const defaults = getDefaultFilters();
    setFilters((prev) =>
      activeTab === "tarefas"
        ? { ...prev, dataInicio: defaults.dataInicio, dataFim: defaults.dataFim }
        : { ...defaults, responsavelIds: prev.responsavelIds }
    );
  };

  if (loading) {
    return <PageLoader label="Carregando dashboard..." />;
  }

  const activeTabLabel = DASHBOARD_PAGE_TABS.find((tab) => tab.value === activeTab)?.label || "Vendas";

  return (
    <div className="min-w-0 space-y-5">
      {/* Filtros em painel lateral, igual à central de notificações. Abre pelo botão da barra
          superior (usePageFilters) ou pelos chips de filtros aplicados. */}
      <FiltersSheet
        open={filtersModalOpen}
        onOpenChange={setFiltersModalOpen}
        description={`Aplicados à aba ${activeTabLabel}.`}
        // Aba Equipe sem admin não tem filtro nenhum: sem botão de limpar.
        onClear={activeTab === "equipe" && !isAdmin ? undefined : handleClearFilters}
        clearDisabled={tabFiltersAreDefault}
      >
            <DashboardFiltersForm
              tab={activeTab}
              filters={filters}
              onChangeFilters={setFilters}
              todayIso={todayIso}
              isAdmin={isAdmin}
              supervisorOptions={supervisorOptions}
              bancoOptions={bancoOptions}
              convenioOptions={convenioOptions}
              tipoProdutoOptions={tipoProdutoOptions}
              sessaoRelatorioRange={sessaoRelatorioRange}
              onChangeSessaoRelatorioRange={setSessaoRelatorioRange}
            />
      </FiltersSheet>

      <div className="flex flex-col gap-3">
        {/* Sem etiqueta de perfil nem botão de filtros aqui: o filtro fica só no ícone da barra
            superior, que mostra a contagem de filtros aplicados. */}
        <DashboardTabs value={activeTab} />

        {activeFilterChips.length ? (
          <div className="flex flex-wrap items-center gap-2" aria-label="Filtros aplicados">
            {activeFilterChips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => setFiltersModalOpen(true)}
                className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {chip}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {activeTab === "vendas" ? (
      <div {...tabPanelProps(DASHBOARD_TABS_ID, "vendas")} className="space-y-5">
      {dashboard?.escopo?.joinDisponivel === false ? (
        <div className="rounded-3xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          Não foi possível consultar a produção Join para este escopo porque os responsáveis filtrados não possuem Supervisor ID configurado.
        </div>
      ) : null}

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Produção</h2>
        </div>

        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
          {producaoCards.map((card) => (
            <MetricTile key={card.label} {...card} tone="gradient" />
          ))}
        </div>

        <AreaTrendCard
          title="Evolução da produção"
          description="Acompanhe a evolução ao longo do período."
          points={producaoEvolucaoCompleta}
          visibleMetrics={visibleAreaMetrics}
          onToggleMetric={toggleAreaMetric}
          metrics={[
            { key: "producaoBruta", label: "Bruta", color: CHART_CATEGORICAL[0], badgeColor: CHART_CATEGORICAL[0] },
            { key: "producaoLiquida", label: "Líquida", color: CHART_CATEGORICAL[1], badgeColor: CHART_CATEGORICAL[1] },
            { key: "producaoComissionada", label: "Comissionada", color: CHART_CATEGORICAL[2], badgeColor: CHART_CATEGORICAL[2] },
          ]}
        />
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <BriefcaseBusiness className="h-5 w-5 text-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Digitação</h2>
        </div>

        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
          {contratosStatusCards.map((card) => (
            <MetricTile key={card.label} {...card} tone="gradient" />
          ))}
        </div>

        <ContractStatusStackedBarCard
          title="Status da digitação no tempo"
          description="Visão por status ao longo do tempo."
          points={contratosStatusEvolucao}
          granularidade={granularidadeEvolucao}
        />
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Rankings</h2>
        </div>

        <PartnerRankingCard
          items={enrichedRankingParceiros}
          rankingMode={partnerRankingMode}
          onRankingModeChange={setPartnerRankingMode}
          onOpenPartner={(partnerId) => navigate(`/parceiros/${partnerId}/vida-agente`)}
        />

        <MonthlyPartnerComparisonCard
          enabled={Boolean(usuario)}
          cacheScope={usuario?.id}
          requestBase={monthlyComparisonFilters}
          endDate={filters.dataFim}
          refreshSignal={dashboard}
          parceiroByJoinId={parceiroByJoinId}
          responsavelById={responsavelById}
          responsavelBySupervisorId={responsavelBySupervisorId}
          onOpenPartner={(partnerId) => navigate(`/parceiros/${partnerId}/vida-agente`)}
        />

        {rankingBancos.length || rankingConvenios.length ? (
          <div className="grid gap-4 xl:grid-cols-2">
            <RankingBarCard
              title="Top bancos"
              subtitle={`Ranking por ${bankRankingMetricLabel.toLowerCase()}.`}
              items={rankingBancos}
              campanhasPorNome={campanhasPorNomeBanco}
              color={CHART_CATEGORICAL[0]}
              formatter={bankRankingMetricFormatter}
              rankingMode={bankRankingMode}
              onRankingModeChange={setBankRankingMode}
            />
            <RankingBarCard
              title="Top convênios"
              subtitle={`Ranking por ${bankRankingMetricLabel.toLowerCase()}.`}
              items={rankingConvenios}
              campanhasPorNome={campanhasPorNomeConvenio}
              color={CHART_CATEGORICAL[0]}
              formatter={bankRankingMetricFormatter}
              rankingMode={bankRankingMode}
              onRankingModeChange={setBankRankingMode}
            />
          </div>
        ) : (
          <Card title="Top bancos e convênios">
            <p className="text-sm text-muted-foreground">
              Em breve
            </p>
          </Card>
        )}
      </section>
      </div>
      ) : null}

      {activeTab === "tarefas" ? (
      <div {...tabPanelProps(DASHBOARD_TABS_ID, "tarefas")} className="space-y-5">
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <BriefcaseBusiness className="h-5 w-5 text-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Tarefas</h2>
        </div>

        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
          {tarefasCards.map((card) => (
            <MetricTile key={card.label} {...card} />
          ))}
        </div>

        <div className="grid gap-4">
          <OperationalAreaCard
            title="Evolução operacional"
            description="Criação, conclusão e atrasos ao longo do tempo."
            points={tarefasEvolucaoCompleta}
            granularidade={granularidadeEvolucao}
          />

          <StatusOverviewCard
            title="Panorama operacional"
            description="Resumo geral do período."
            items={statusItems}
            total={statusItemsTotal}
          />
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <TaskBreakdownDonutCard
            title="Tipos de tarefa"
            subtitle="Tipos das tarefas concluídas."
            items={tiposTarefaItems}
            centerLabel="Concluídas"
            emptyLabel="Sem tarefas concluídas no período selecionado."
          />
          <TaskBreakdownDonutCard
            title="Motivos de tarefa"
            subtitle="Motivos das tarefas concluídas."
            items={motivosTarefaItemsFiltrados}
            centerLabel="Motivos"
            emptyLabel="Sem motivos registrados em tarefas concluídas no período selecionado."
            headerExtra={
              <div className="min-w-[220px]">
                <label className="sr-only" htmlFor="motivo-tipo-select">
                  Filtrar por tipo
                </label>
                <select
                  id="motivo-tipo-select"
                  className="h-9 w-full rounded-full border border-border/70 bg-background/40 px-3 text-xs text-foreground shadow-sm outline-none transition focus:border-primary/50 focus:ring-2 focus:ring-ring/30"
                  value={motivoTipoSelecionado}
                  onChange={(event) => setMotivoTipoSelecionado(event.target.value)}
                >
                  {tipoMotivoOptions.map((option) => (
                    <option key={String(option.value)} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            }
          />
        </div>
      </section>
      </div>
      ) : null}

      {activeTab === "equipe" ? (
      <div {...tabPanelProps(DASHBOARD_TABS_ID, "equipe")} className="space-y-5">
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Login e atividade</h2>
          </div>
          <span className="text-xs text-muted-foreground">Atualiza a cada {DASHBOARD_AUTO_REFRESH_SECONDS}s.</span>
        </div>

        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
          <MetricTile
            icon={Users}
            label="Usuários online"
            value={formatNumber(onlineUsers.length)}
            helper={`${formatNumber(monitoredUsers.length)} monitorados`}
            accent="hsl(var(--badge-success))"
          />
          <MetricTile
            icon={Clock3}
            label="Tempo médio logado"
            value={formatElapsedSeconds(averageSessionSeconds)}
            helper="Sessões ativas"
            accent="var(--chart-1)"
          />
          <MetricTile
            icon={TrendingUp}
            label="Maior sessão ativa"
            value={longestSessionUser ? formatElapsedSeconds(longestSessionUser.tempoLogadoSegundos) : "N/A"}
            helper={longestSessionUser ? longestSessionUser.nome : "Nenhuma sessão ativa agora."}
            accent="var(--chart-4)"
          />
        </div>

        <Card title="Equipe monitorada">
          <div className={`grid gap-3 ${monitoredUserColumns.length > 1 ? "xl:grid-cols-2" : ""}`}>
            {monitoredUsers.length ? (
              monitoredUserColumns.map((column, columnIndex) => (
                <div key={`team-column-${columnIndex}`} className="space-y-3">
                  {column.map((item) => <SessionUserRow key={item.id} user={item} />)}
                </div>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                Nenhum usuario localizado para este escopo.
              </div>
            )}
          </div>
        </Card>

        {isAdmin ? (
          <Card title="Relatório de sessões">
            <div className="grid gap-4">
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
                <MetricTile
                  icon={Clock3}
                  label="Tempo médio por sessão"
                  value={loadingSessaoRelatorio ? "..." : formatElapsedSeconds(sessaoRelatorio?.tempoMedioSegundos || 0)}
                  helper="Média por sessão"
                  accent="var(--chart-2)"
                />
                <MetricTile
                  icon={Activity}
                  label="Tempo total logado"
                  value={loadingSessaoRelatorio ? "..." : formatElapsedSeconds(sessaoRelatorio?.tempoTotalSegundos || 0)}
                  helper="Tempo total"
                  accent="var(--chart-5)"
                />
                <MetricTile
                  icon={Users}
                  label="Sessões no período"
                  value={
                    loadingSessaoRelatorio ? (
                      "..."
                    ) : (
                      <NumberFlowValue className="tabular-nums" value={sessaoRelatorio?.sessoesTotal || 0} format={{ maximumFractionDigits: 0 }} />
                    )
                  }
                  helper="Total de sessões"
                  accent="hsl(var(--badge-info))"
                />
              </div>

              <div className="min-w-0 rounded-3xl border border-border/70 bg-background/40 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Total por comercial</p>
                    <p className="mt-1 text-xs text-muted-foreground">Por tempo total de login.</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {sessaoRelatorio?.dataInicio && sessaoRelatorio?.dataFim
                      ? `${sessaoRelatorio.dataInicio} até ${sessaoRelatorio.dataFim}`
                      : ""}
                  </p>
                </div>

                <div className="mt-3 overflow-x-auto">
                  {loadingSessaoRelatorio ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">Carregando relatório...</div>
                  ) : (sessaoRelatorio?.usuarios || []).length ? (
                    <table className="w-full min-w-[560px] border-separate border-spacing-y-2 text-sm">
                      <thead>
                        <tr className="text-xs text-muted-foreground">
                          <th className="px-3 text-left font-medium">#</th>
                          <th className="px-3 text-left font-medium">Comercial</th>
                          <th className="px-3 text-right font-medium">Tempo total</th>
                          <th className="px-3 text-right font-medium">Sessões</th>
                          <th className="px-3 text-right font-medium">Tempo médio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sessaoRelatorio.usuarios || []).map((item, index) => (
                          <tr key={String(item.usuarioId)} className="rounded-2xl bg-card/60 text-foreground shadow-sm">
                            <td className="px-3 py-2 text-muted-foreground">{formatNumber(index + 1)}</td>
                            <td className="px-3 py-2 font-medium">{item.usuarioNome || `Usuario ${item.usuarioId}`}</td>
                            <td className="px-3 py-2 text-right">{formatElapsedSeconds(item.tempoTotalSegundos || 0)}</td>
                            <td className="px-3 py-2 text-right">{formatNumber(item.sessoesTotal || 0)}</td>
                            <td className="px-3 py-2 text-right">{formatElapsedSeconds(item.tempoMedioSegundos || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="py-6 text-center text-sm text-muted-foreground">Sem sessões encontradas no período.</div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ) : null}
      </section>
      </div>
      ) : null}
    </div>
  );
}
